// Flat dot-key dictionaries for the three supported locales. `en` is the
// canonical key set; `satisfies Record<MessageKey, string>` makes a missing
// de/fr key a compile error, so the dictionaries cannot drift apart silently.
//
// Interpolation is {name}-style, resolved by t() in lib/i18n.tsx.
// Wire values (enum members, GraphQL fields) are never translated — the
// `enum.*` keys translate them at the display layer only (labelizeT).

export const en = {
  // Navigation / layout
  "nav.map": "Map",
  "nav.feed": "Feed",
  "nav.discover": "Discover",
  "nav.calendar": "Calendar",
  "nav.new": "New",
  "nav.ticklist": "Ticklist",
  "nav.saved": "Saved",
  "nav.profile": "Profile",
  "nav.moderation": "Moderation",
  "nav.myProfile": "My profile",
  "nav.notFound": "Page not found.",

  // Theme toggle
  "theme.system": "System theme",
  "theme.light": "Light theme",
  "theme.dark": "Dark theme",
  "theme.toggleTitle": "{current} — click for {next}",
  "theme.toggleAria": "Theme: {current}. Switch to {next}",

  // Locale switcher
  "locale.label": "Language",

  // Shared
  "common.loading": "Loading…",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.delete": "Delete",
  "common.deleting": "Deleting…",
  "common.remove": "Remove",
  "common.undo": "Undo",
  "common.done": "Done",
  "common.sort": "Sort",
  "common.name": "Name",
  "common.type": "Type",
  "common.any": "Any",
  "common.notSpecified": "Not specified",
  "common.logIn": "Log in",
  "common.register": "Register",
  "common.someone": "Someone",
  "common.unnamed": "Unnamed adventurer",
  "common.by": "by",

  // Enum display values (wire values stay UPPERCASE on the wire)
  "enum.HIKING": "Hiking",
  "enum.TRAIL_RUNNING": "Trail running",
  "enum.SNOWSHOEING": "Snowshoeing",
  "enum.SKI_TOURING": "Ski touring",
  "enum.MOUNTAINEERING": "Mountaineering",
  "enum.ROCK_CLIMBING": "Rock climbing",
  "enum.ICE_CLIMBING": "Ice climbing",
  "enum.VIA_FERRATA": "Via ferrata",
  "enum.SUMMIT": "Summit",
  "enum.HUT": "Hut",
  "enum.PASS": "Pass",
  "enum.CRAG": "Crag",
  "enum.VIEWPOINT": "Viewpoint",
  "enum.PARKING": "Parking",
  "enum.EXCELLENT": "Excellent",
  "enum.GOOD": "Good",
  "enum.MEDIOCRE": "Mediocre",
  "enum.POOR": "Poor",
  "enum.NEXT_UP": "Next up",
  "enum.THIS_SEASON": "This season",
  "enum.SOMEDAY": "Someday",
  "enum.ONSIGHT": "Onsight",
  "enum.FLASH": "Flash",
  "enum.REDPOINT": "Redpoint",
  "enum.ATTEMPT": "Attempt",
  "enum.PUBLIC": "Public",
  "enum.FOLLOWERS": "Followers",
  "enum.PRIVATE": "Private",
  "enum.OWNER": "Owner",
  "enum.PARTICIPANT": "Participant",
  "enum.OPEN": "Open",
  "enum.RESOLVED": "Resolved",
  "enum.DISMISSED": "Dismissed",
  "enum.ACTIVE": "Active",
  "enum.SUSPENDED": "Suspended",
  "enum.BANNED": "Banned",
  "enum.DELETED": "Deleted",
  "enum.PLACE": "Place",
  "enum.REGION": "Region",
  "enum.ROUTE": "Route",
  "enum.OUTING": "Outing",
  "enum.PERSON": "Person",
  "enum.COMMENT": "Comment",
  "enum.MAIN": "Main",
  "enum.START": "Start",
  "enum.HIDDEN": "Hidden",
  "enum.REQUEST_TO_JOIN": "Request to join",
  "enum.INVITE_ONLY": "Invite only",
  "enum.ADMIN": "Admin",
  "enum.MEMBER": "Member",
  "enum.PENDING": "Pending",
  "enum.INVITED": "Invited",
  "enum.MEMBERS": "Members",
  "enum.MEMBERS_ONLY": "Members only",
  "enum.CLOSED": "Closed",
  "enum.ORGANIZER": "Organizer",
  "enum.ATTENDEE": "Attendee",
  "enum.CONFIRMED": "Confirmed",
  "enum.DECLINED": "Declined",
  "enum.NONE": "None",
  "enum.DAILY": "Daily",
  "enum.WEEKLY": "Weekly",
  "enum.BIWEEKLY": "Every two weeks",
  "enum.MONTHLY": "Monthly",
  "enum.PLANNED": "Planned",
  "enum.COMPLETED": "Completed",

  // Stat labels
  "stat.conditions": "Conditions",
  "stat.time": "Time",
  "stat.distance": "Distance",
  "stat.ascent": "Ascent",
  "stat.descent": "Descent",
  "stat.movingTime": "Moving time",
  "stat.pace": "Pace",
  "stat.duration": "Duration",
  "stat.elevation": "Elevation",
  "stat.minElevation": "Min elevation",
  "stat.maxElevation": "Max elevation",
  "stat.grade": "Grade ({activity})",

  // Entity page tabs
  "tabs.info": "Info",
  "tabs.map": "Map",
  "tabs.photos": "Photos",
  "tabs.places": "Places",
  "tabs.routes": "Routes",
  "tabs.outings": "Outings",
  "tabs.comments": "Comments",
  "tabs.subregions": "Subregions",
  "tabs.profile": "Profile",
  "tabs.followers": "Followers",
  "tabs.following": "Following",
  "tabs.ticklist": "Ticklist",
  "tabs.members": "Members",
  "tabs.events": "Events",
  "tabs.participants": "Participants",

  // Login gate
  "gate.needAccount": "You need an account to {action}. Browsing is open to everyone — creating things needs a login.",
  "gateAction.logOuting": "log an outing",
  "gateAction.keepTicklist": "keep a ticklist",
  "gateAction.keepSaved": "save places on the map",
  "gateAction.seeProfile": "see your profile",
  "gateAction.seeCalendar": "see your calendar",
  "gateAction.addPlace": "add a place",
  "gateAction.drawRoute": "draw a route",
  "gateAction.editOuting": "edit an outing",
  "gateAction.createGroup": "create a group",
  "gateAction.editGroup": "edit a group",
  "gateAction.createEvent": "plan an event",
  "gateAction.editEvent": "edit an event",

  // Auth pages
  "auth.email": "Email",
  "auth.password": "Password",
  "login.title": "Log in",
  "login.busy": "Logging in…",
  "login.failed": "Login failed",
  "login.forgot": "Forgot password?",
  "login.noAccount": "No account?",
  "login.demo": "Demo login: demo@gaea.dev / demo-password",
  "register.title": "Register",
  "register.displayName": "Display name",
  "register.passwordMin": "Password (min 8 characters)",
  "register.tooShort": "Password must be at least 8 characters.",
  "register.busy": "Creating account…",
  "register.submit": "Create account",
  "register.failed": "Registration failed",
  "register.haveAccount": "Already have an account?",
  "forgot.title": "Reset password",
  "forgot.sent": "If that address has an account, a reset link has been sent.",
  "forgot.devNote": "In development the link isn't emailed — it appears in the auth service logs.",
  "forgot.intro": "Enter your account email and we'll send you a link to reset your password.",
  "forgot.busy": "Sending…",
  "forgot.submit": "Send reset link",
  "forgot.remembered": "Remembered it?",
  "forgot.failed": "Request failed",
  "reset.title": "Choose a new password",
  "reset.invalid": "This reset link is invalid or has expired.",
  "reset.requestNew": "Request a new reset link",
  "reset.newPassword": "New password (min 8 characters)",
  "reset.confirmPassword": "Confirm new password",
  "reset.mismatch": "Passwords do not match.",
  "reset.busy": "Resetting…",
  "reset.submit": "Reset password",
  "reset.failed": "Reset failed",
  "verify.title": "Verify your email",
  "verify.invalid": "This verification link is invalid or has expired.",
  "verify.requestNewPre": "You can request a new one from",
  "verify.yourProfile": "your profile",
  "verify.done": "Your email is verified. Thanks!",
  "verify.goProfile": "Go to your profile",
  "verify.intro": "Click below to confirm this email address for your account.",
  "verify.busy": "Confirming…",
  "verify.submit": "Confirm my email",
  "verify.failed": "Verification failed",

  // Feed
  "feed.title": "Recent outings",
  "feed.everyone": "Everyone",
  "feed.following": "Following",
  "feed.since": "Since",
  "feed.minRating": "Min rating",
  "feed.sortNew": "Newest",
  "feed.sortOld": "Oldest",
  "feed.sortTop": "Highest rated",
  "feed.updating": "Updating…",
  "feed.loginToSeeFollowing": "to see outings from people you follow.",
  "feed.notFollowingAnyone": "You aren't following anyone yet. Open an outing and visit its author's profile to follow them.",
  "feed.emptyFollowing": "No outings from people you follow match these filters.",
  "feed.emptyAll": "No outings match these filters.",
  "feed.loadingMore": "Loading more…",
  "feed.endOne": "That's all — {count} outing.",
  "feed.endOther": "That's all — {count} outings.",

  // Outing card
  "card.privateTitle": "Private — only you can see this outing",
  "card.privateAria": "Private outing",
  "card.followersTitle": "Visible to the owner's followers only",
  "card.followersAria": "Followers-only outing",
  "card.plannedTitle": "Planned outing — visible only to you",
  "card.moreRoutes": "+{count} more",
  "card.conditions": "Conditions: {value}",
  "card.commentsOne": "{count} comment",
  "card.commentsOther": "{count} comments",

  // Like / follow
  "like.ariaLike": "Like this outing ({count} likes)",
  "like.ariaUnlike": "Unlike this outing ({count} likes)",
  "like.ariaLogin": "Log in to like this outing ({count} likes)",
  "follow.follow": "Follow",
  "follow.following": "Following",

  // Notifications
  "notif.title": "Notifications",
  "notif.ariaCount": "Notifications ({count} unread)",
  "notif.markAll": "Mark all read",
  "notif.marking": "Marking…",
  "notif.loadError": "Couldn't load notifications.",
  "notif.empty": "Nothing yet — likes, comments and new followers show up here.",
  "notif.OUTING_LIKE": "liked your outing",
  "notif.OUTING_COMMENT": "commented on your outing",
  "notif.PERSON_FOLLOW": "followed you",
  "notif.CLUB_INVITE": "invited you to join a group",
  "notif.CLUB_JOIN_REQUEST": "asked to join your group",
  "notif.CLUB_JOIN_APPROVED": "approved your request to join a group",
  "notif.EVENT_INVITE": "invited you to an event",

  // Search box
  "search.placeholder": "Search places, routes, people…",
  "search.sectionPlaces": "Places",
  "search.sectionRegions": "Regions",
  "search.sectionRoutes": "Routes",
  "search.sectionPeople": "People",
  "search.sectionOutings": "Outings",
  "search.nearbyPlaces": "Nearby places",
  "search.nearbyRoutes": "Nearby routes",
  "search.nearMapCenter": "Near map center",
  "search.nearMe": "Near me",
  "search.locating": "Locating…",
  "search.geoError": "Couldn't get your location — allow location access and try again.",
  "search.findingNearby": "Finding what's nearby…",
  "search.nearbyError": "Couldn't load nearby results.",
  "search.nothingNearby": "Nothing nearby.",
  "search.searching": "Searching…",
  "search.noMatches": "No matches.",
  "search.showOnMap": "Show on map",
  "search.person": "Person",
  "search.unnamed": "Unnamed",
  "search.away": "{distance} away",

  // Map page
  "map.addPlaceHere": "Add a place here?",
  "map.add": "Add",
  "map.planEventHere": "Plan an event here",
  "map.saveSpotHere": "Save this spot",
  "map.spotNamePlaceholder": "e.g. Secret viewpoint",
  "map.savedPlaces": "Saved",
  "map.savedEmpty": "Nothing saved yet — use “Save” on a place page or right-click the map.",
  "map.manageSaved": "All saved places",
  "map.filters": "Filters",
  "map.drawRoute": "Draw a route",
  "map.zoomForData": "Zoom in to see map data",
  "map.zoomForRoutes": "Zoom in to see routes",
  "map.truncated": "Showing {shown} of {total} {layer}",
  "map.truncatedMore": " (+{count} more)",
  "map.truncatedZoom": " — zoom in for the rest",
  "map.loadError": "Failed to load map data",
  "map.layerPlaces": "places",
  "map.layerRoutes": "routes",
  "map.layerRegions": "regions",
  "map.layerOutings": "outings",
  "map.layerEvents": "events",

  // Filter sheet
  "filters.title": "Display & filters",
  "filters.closeAria": "Close filters",
  "filters.showOnMap": "Show on map",
  "filters.outings": "Outings",
  "filters.routes": "Routes",
  "filters.routeTracks": "Tracks",
  "filters.regions": "Regions",
  "filters.events": "Events",
  "filters.tracksHint": "Tracks draw from zoom 11 — zoom in if you don't see them.",
  "filters.clustering": "Cluster nearby pins",
  "filters.routeActivities": "Route activities",
  "filters.placeTypes": "Place types",
  "filters.elevMin": "Place elevation ≥ (m)",
  "filters.elevMax": "Place elevation ≤ (m)",
  "filters.outingsFrom": "Outings from",
  "filters.outingsUntil": "Outings until",
  "filters.distMin": "Route distance ≥ (km)",
  "filters.distMax": "Route distance ≤ (km)",
  "filters.ascentMin": "Route ascent ≥ (m)",
  "filters.nameContains": "Route name contains",
  "filters.clear": "Clear filters",

  // Map tooltip
  "tooltip.clickForDetails": "Click for details",

  // Profile activity map
  "profileMap.title": "Activity map",
  "profileMap.outingsOne": "{count} outing",
  "profileMap.outingsOther": "{count} outings",
  "profileMap.w30d": "30 days",
  "profileMap.w3m": "3 months",
  "profileMap.w12m": "12 months",
  "profileMap.wAll": "All time",
  "profileMap.empty": "No mapped outings in this window.",

  // Profile page
  "profile.loadError": "Could not load your profile.",
  "profile.editName": "Edit display name",
  "profile.publicProfile": "Public profile",
  "profile.logout": "Log out",
  "profile.myOutings": "My outings",
  "profile.noOutings": "No outings yet — log your first from a route page or the New tab.",
  "profile.joinedAs": "Joined as participant",
  "profile.changePassword": "Change password",
  "avatar.change": "Change photo",
  "avatar.upload": "Upload photo",
  "avatar.uploadFailed": "Upload failed",
  "email.verified": "verified",
  "email.notVerified": "Email not verified",
  "email.sentNote": "Sent — check your inbox. (In development the link isn't emailed — it appears in the auth service logs.)",
  "email.send": "Send verification email",
  "email.sending": "Sending…",
  "email.sendFailed": "Could not send the email",
  "pw.current": "Current password",
  "pw.new": "New password (min 8 characters)",
  "pw.confirm": "Confirm new password",
  "pw.tooShort": "New password must be at least 8 characters.",
  "pw.mismatch": "New passwords do not match.",
  "pw.wrongCurrent": "Current password is incorrect.",
  "pw.failed": "Password change failed",
  "pw.changed": "Password changed.",
  "pw.changing": "Changing…",
  "pw.submit": "Change password",

  // Person page
  "person.notFound": "No such person.",
  "person.joined": "Joined {date}",
  "person.activities": "Activities",
  "person.noneYetSelf": "None yet — pick your activities from your profile.",
  "person.noneListed": "None listed.",
  "person.noOutings": "No outings logged yet.",
  "person.noFollowers": "No followers yet.",
  "person.notFollowingSelf": "You aren't following anyone yet.",
  "person.notFollowing": "Not following anyone yet.",

  // Ticklist page
  "ticklist.title": "Ticklist",
  "ticklist.intro": "Routes you want to do. Ticks you mark public appear on your profile — everything else only you can see.",
  "ticklist.empty": "Nothing on your ticklist yet — open a route and hit “Ticklist”.",
  "ticklist.browseMap": "Browse the map",
  "ticklist.done": "Done",
  "ticklist.doneOn": "Done {date}",
  "ticklist.log": "Log",

  // Saved places
  "savePlace.button": "Save",
  "savePlace.saved": "Saved",
  "savePlace.buttonTitle": "Save this place for quick access on the map",
  "savePlace.savedTitle": "Remove from your saved places",
  "saved.title": "Saved places",
  "saved.intro": "Places and map spots you saved for quick access. Only you can see them.",
  "saved.tabPlaces": "Places",
  "saved.tabActivity": "Nearby activity",
  "saved.empty": "Nothing saved yet — hit “Save” on a place page, or right-click anywhere on the map.",
  "saved.browseMap": "Browse the map",
  "saved.customPin": "Custom spot",
  "saved.showOnMap": "Map",
  "saved.showOnMapTitle": "Show on the map",
  "saved.savedOn": "Saved {date}",
  "saved.allSaved": "All saved",
  "saved.nearbyNote": "Recent outings within ~10 km of your saved places.",
  "saved.emptyActivity": "No recent outings near your saved places yet.",
  "saved.renameTitle": "Rename",
  "saved.removeTitle": "Remove from saved",
  "saved.latest": "Showing the latest {count} outings.",
  "tick.priorityAria": "Priority",
  "tick.publicTitle": "Public — shown on your profile. Click to make private.",
  "tick.privateTitle": "Private — only you can see it. Click to show on your profile.",
  "tick.makePrivate": "Make tick private",
  "tick.makePublic": "Make tick public",
  "tick.removeTitle": "Remove from ticklist",

  // Tick button / popover
  "tick.button": "Ticklist",
  "tick.priority": "Priority",
  "tick.targetSeason": "Target season",
  "tick.seasonPlaceholder": "summer 2027",
  "tick.note": "Private note",
  "tick.notePlaceholder": "Partner, gear, conditions to wait for…",
  "tick.add": "Add to ticklist",

  // Place page
  "place.notFound": "Place not found.",
  "place.addedBy": "Added by",
  "place.editPlace": "Edit place",
  "place.confirmDelete": "Delete this place? This cannot be undone.",
  "place.elevationOptional": "Elevation (m, optional)",
  "place.sortLinked": "Linked first",
  "place.sortDistance": "Distance (shortest)",
  "place.sortAscent": "Ascent (most)",
  "place.linked": "linked",
  "place.passes": "passes {distance} away",
  "place.noRoutes": "No routes pass this place yet.",

  // New place page
  "newPlace.heading": "New place",
  "newPlace.title": "Add a place",
  "newPlace.pickPre": "Pick a spot first — right-click (or long-press) the",
  "newPlace.pickMap": "map",
  "newPlace.pickPost": "where the place belongs.",
  "newPlace.namePlaceholder": "e.g. Sněžka",
  "newPlace.busy": "Adding…",
  "newPlace.submit": "Add place",

  // Region page
  "region.kicker": "Region",
  "region.notFound": "Region not found.",
  "region.partOf": "Part of",
  "region.noSubregions": "No subregions.",
  "region.sortElevation": "Elevation (highest)",
  "region.noPlaces": "No places recorded in this region yet (membership is computed from geometry).",

  // Route page
  "route.kicker": "Route",
  "route.notFound": "Route not found.",
  "route.editDetails": "Edit details",
  "route.logOuting": "Log outing",
  "route.ratingsOne": "({count} rating)",
  "route.ratingsOther": "({count} ratings)",
  "route.wantOne": "{count} person wants to do this route",
  "route.wantOther": "{count} people want to do this route",
  "route.addedBy": "Added by",
  "route.aMember": "a member",
  "route.related": "Related routes",
  "route.noPlaces": "No places linked to this route yet.",
  "route.photoSample": "Showing a sample from recent outings — open an outing for all of its photos.",
  "route.noPhotos": "No photos yet — photos added to outings on this route show up here.",
  "route.noOutings": "No outings on this route yet.",
  "route.beFirst": "Be the first!",
  "route.confirmDelete": "Delete route “{name}”? This can't be undone.",
  "route.fkDelete": "This route is referenced by outings and can't be deleted.",

  // Outing page
  "outing.kicker": "Outing",
  "outing.notFound": "Outing not found.",
  "outing.confirmDelete": "Delete this outing? This cannot be undone.",
  "outing.privateTitle": "Only you can see this outing",
  "outing.followersTitle": "Visible to the owner's followers only",
  "outing.plannedTitle": "Planned — visible only to you until you mark it completed",
  "outing.markCompleted": "Mark as completed",
  "outing.party": "Party",
  "outing.noGeometry": "No track or route geometry for this outing.",
  "outing.noPhotos": "No photos for this outing.",
  "outing.noRoutes": "No routes linked to this outing.",
  "outing.edit": "Edit",

  // Comments
  "comments.back": "← Back to all comments",
  "comments.replyingUnder": "Replying under",
  "comments.deleted": "[deleted]",
  "comments.empty": "No comments yet. Start the conversation.",
  "comments.author": "Author",
  "comments.wasDeleted": "This comment was deleted.",
  "comments.reply": "Reply",
  "comments.confirmDelete": "Delete this comment? Replies to it will stay visible.",
  "comments.continueOne": "Continue this thread ({count} reply)",
  "comments.continueOther": "Continue this thread ({count} replies)",
  "comments.replyPlaceholder": "Write a reply…",
  "comments.addPlaceholder": "Add a comment…",
  "comments.posting": "Posting…",
  "comments.submit": "Comment",
  "comments.loginToJoin": "to join the discussion.",

  // Shared form labels
  "form.title": "Title",
  "form.name": "Name",
  "form.date": "Date",
  "form.activity": "Activity",
  "form.activities": "Activities",
  "form.conditions": "Conditions",
  "form.rating": "Rating",
  "form.notes": "Notes",
  "form.description": "Description",
  "form.visibility": "Visibility",
  "form.status": "Status",
  "form.durationOptional": "Duration (minutes, optional)",
  "form.pickActivity": "Pick at least one activity.",

  // New / edit outing
  "newOuting.title": "Log an outing",
  "newOuting.titlePlaceholder": "e.g. Sněžka sunrise loop",
  "newOuting.routesOptional": "Routes (optional)",
  "newOuting.gpxOptional": "GPX track (optional)",
  "newOuting.timeHours": "Time (hours)",
  "newOuting.notesPlaceholder": "Conditions, highlights, warnings for others…",
  "newOuting.photosOptional": "Photos (optional)",
  "newOuting.participantsOptional": "Participants (optional)",
  "newOuting.followersHint": "Followers: only people who follow you.",
  "newOuting.plannedHint": "A planned outing is visible only to you until you mark it completed.",
  "newOuting.ownerNote": "You'll be recorded as the owner.",
  "newOuting.submit": "Save outing",
  "newOuting.errTitle": "Give your outing a title.",
  "newOuting.errDate": "When did you go?",
  "newOuting.errFuture": "A completed outing can't be in the future — mark it as planned instead.",
  "newOuting.errElapsed": "Time must be a positive number of hours.",
  "err.owner": "Something went wrong linking you as the owner of this outing. Please try again.",
  "err.permission": "You don't have permission to do that — try logging in again.",
  "err.rejected": "The server rejected the data: {msg}",
  "gpx.stats": "{count} points · ~{distance}",
  "gpx.removeAria": "Remove GPX file",
  "edit.title": "Edit outing",
  "edit.notOwner": "Only the owner of this outing can edit it.",
  "edit.noPermission": "You don't have permission to edit this outing.",
  "edit.gpx": "GPX track",
  "edit.replacesOnSave": "replaces the current track on save",
  "edit.cancelTrackAria": "Cancel track change",
  "edit.trackRemoved": "Track will be removed on save.",
  "edit.trackAttached": "Track attached · {distance} / {ascent} ↑",
  "edit.replace": "Replace",
  "edit.photos": "Photos",
  "edit.routes": "Routes",
  "edit.participants": "Participants",
  "edit.submit": "Save changes",
  "photo.removedUndo": "Removed — undo",
  "photo.removeAria": "Remove photo",
  "photo.caption": "Caption (optional)",
  "photo.add": "Add photos",
  "photo.addMore": "Add more photos",
  "photo.closeAria": "Close photo",
  "photo.label": "Photo",

  // New route page
  "newRoute.heading": "New route",
  "newRoute.title": "Draw a route",
  "newRoute.drawTab": "Draw on map",
  "newRoute.uploadTab": "Upload GPX",
  "newRoute.gpxFile": "GPX file",
  "newRoute.pointsOne": "{count} point",
  "newRoute.pointsOther": "{count} points",
  "newRoute.undo": "Undo",
  "newRoute.undoTitle": "Undo last point (Ctrl+Z)",
  "newRoute.clear": "Clear",
  "newRoute.drawHelp": "Click to add points · drag a point to move it · click one, then Delete to remove it",
  "newRoute.uploadHint": "Upload a GPX file to preview it here.",
  "newRoute.noElevNote": "Drawn routes have no elevation data — ascent shows as 0. Elevation comes from GPX uploads.",
  "newRoute.namePlaceholder": "e.g. Sněžka via Obří důl",
  "newRoute.linkedPlaces": "Linked places (optional)",
  "newRoute.errName": "Give the route a name.",
  "newRoute.errGeomDraw": "Draw at least two points on the map.",
  "newRoute.errGeomUpload": "Upload a GPX file first.",
  "newRoute.errDuration": "Duration must be a positive number of minutes.",
  "newRoute.busy": "Creating…",
  "newRoute.submit": "Create route",

  // Groups (clubs)
  "group.kicker": "Group",
  "group.notFound": "Group not found.",
  "group.membersOne": "{count} member",
  "group.membersOther": "{count} members",
  "group.created": "Created {date}",
  "group.joinPolicy": "Join policy",
  "group.join": "Join group",
  "group.requestJoin": "Request to join",
  "group.requestPending": "Request pending",
  "group.withdrawRequest": "Withdraw request",
  "group.inviteOnlyNote": "Invite only — an admin has to invite you.",
  "group.invitedBanner": "You've been invited to this group.",
  "group.acceptInvite": "Accept invitation",
  "group.decline": "Decline",
  "group.leave": "Leave group",
  "group.confirmLeave": "Leave this group?",
  "group.bannedNote": "You have been banned from this group.",
  "group.edit": "Edit group",
  "group.confirmDelete": "Delete this group? This cannot be undone.",
  "group.noDescription": "No description yet.",
  "group.noOutings": "No outings shared with this group yet.",
  "group.membersHidden": "The member list is only visible to members.",
  "group.eventsSoon": "Group events are coming soon.",
  "group.pendingRequests": "Pending requests",
  "group.approve": "Approve",
  "group.promote": "Make admin",
  "group.demote": "Remove admin",
  "group.ban": "Ban",
  "group.unban": "Unban",
  "group.confirmBan": "Ban {name} from this group?",
  "group.confirmKick": "Remove {name} from this group?",
  "group.invite": "Invite people",
  "group.newEvent": "New event",
  "group.noUpcomingEvents": "No upcoming events.",

  // New / edit group
  "newGroup.title": "Create a group",
  "newGroup.namePlaceholder": "e.g. Alpine Wanderers",
  "newGroup.errName": "Give the group a name.",
  "newGroup.visibilityHint": "Hidden: only members can see the group.",
  "newGroup.ownerNote": "You'll be recorded as the group's owner.",
  "newGroup.submit": "Create group",
  "editGroup.adminsOnly": "Only group admins can edit this group.",

  // Share outing to group
  "share.label": "Share to group",
  "share.select": "Choose a group…",
  "share.removeAria": "Unshare from {name}",
  "share.button": "Share",
  "share.copied": "Link copied",

  // Events
  "event.kicker": "Event",
  "event.notFound": "Event not found.",
  "event.invitedBanner": "You've been invited to this event.",
  "event.acceptInvite": "Accept invitation",
  "event.join": "Join event",
  "event.requestJoin": "Request to join",
  "event.requestPending": "Request pending",
  "event.withdrawRequest": "Withdraw request",
  "event.youAreGoing": "You're going",
  "event.cantGo": "Can't go",
  "event.youDeclined": "You declined",
  "event.imGoing": "I'm going",
  "event.membersOnlyPre": "Only members of",
  "event.theGroup": "the group",
  "event.membersOnlyPost": "can join this event.",
  "event.inviteOnlyNote": "Invite only — an organizer has to invite you.",
  "event.closedNote": "Attendance is managed by the organizers.",
  "event.addToCalendar": "Add to calendar",
  "event.edit": "Edit",
  "event.confirmDelete": "Delete this event? This cannot be undone.",
  "event.repeats": "Repeats: {freq}",
  "event.repeatsUntil": "Repeats: {freq} (until {date})",
  "event.organizedBy": "Organized by",
  "event.goingCount": "{count} going",
  "event.goingCap": "{count} / {cap} going",
  "event.fullHint": "Full",
  "event.noDescription": "No description yet.",
  "event.maxParticipants": "Max participants",
  "event.recurrence": "Recurrence",
  "event.meetingPoint": "Meeting point",
  "event.noRoutes": "No routes planned for this event yet.",
  "event.noGeometry": "No location or route geometry for this event.",
  "event.organizers": "Organizers",
  "event.goingSection": "Going",
  "event.nobodyYet": "Nobody has joined yet.",
  "event.confirmRemove": "Remove {name} from this event?",

  // Calendar
  "calendar.today": "Today",
  "calendar.prevMonth": "Previous month",
  "calendar.nextMonth": "Next month",
  "calendar.legendOwn": "Your outings",
  "calendar.legendFollowed": "People you follow",
  "calendar.legendEvent": "Events",
  "calendar.more": "+{count} more",
  "calendar.dayEmpty": "Nothing on this day.",
  "calendar.monthEmpty": "Nothing this month yet.",

  // Discover
  "discover.title": "Discover",
  "discover.groups": "Groups",
  "discover.events": "Events",
  "discover.searchGroups": "Search groups…",
  "discover.searchEvents": "Search events…",
  "discover.newGroup": "New group",
  "discover.newEvent": "New event",
  "discover.yourGroups": "Your groups",
  "discover.yourEvents": "Your events",
  "discover.invited": "Invited",
  "discover.hiddenGroup": "Hidden group",
  "discover.hiddenEvent": "Hidden event",
  "discover.noGroups": "No groups found.",
  "discover.noEvents": "No upcoming events found.",
  "discover.myGroupsOnly": "In my groups",
  "discover.notInAnyGroup": "You're not in any group yet.",

  // New / edit event
  "newEvent.title": "Plan an event",
  "newEvent.titlePlaceholder": "e.g. Sunrise hike to Croix de Fer",
  "newEvent.descriptionPlaceholder": "Plan, meeting details, what to bring…",
  "newEvent.start": "Starts",
  "newEvent.endOptional": "Ends (optional)",
  "newEvent.groupOptional": "Group (optional)",
  "newEvent.noGroup": "No group — standalone event",
  "newEvent.groupHint": "Picking a group unlocks the members-only visibility and join policy.",
  "newEvent.repeatsUntil": "Repeats until",
  "newEvent.maxOptional": "Max participants (optional)",
  "newEvent.meetingPointOptional": "Meeting point (optional)",
  "newEvent.mapPoint": "Map point: {lat}, {lng}",
  "newEvent.clearPointAria": "Clear map point",
  "newEvent.showOnMap": "Show on map",
  "newEvent.showOnMapHint": "Upcoming events with a meeting point appear on the map.",
  "newEvent.routesOptional": "Planned routes (optional)",
  "newEvent.organizerNote": "You'll be recorded as an organizer.",
  "newEvent.submit": "Create event",
  "newEvent.errTitle": "Give the event a title.",
  "newEvent.errStart": "When does it start?",
  "newEvent.errEnd": "The end must be after the start.",
  "newEvent.errCap": "Max participants must be a positive number.",
  "editEvent.title": "Edit event",
  "editEvent.notOrganizer": "Only organizers can edit this event.",
  "editEvent.noPermission": "You don't have permission to edit this event.",

  // Admin / moderation
  "admin.title": "Moderation queue",
  "admin.reportsOne": "{count} report",
  "admin.reportsOther": "{count} reports",
  "admin.all": "All",
  "admin.accountUpdated": "Account status updated.",
  "admin.loadingReports": "Loading reports…",
  "admin.noReports": "No matching reports.",
  "admin.view": "View {kind}",
  "admin.reportedBy": "Reported by",
  "admin.resolve": "Resolve",
  "admin.dismiss": "Dismiss",
  "admin.account": "Account: {status}",
  "admin.suspend": "Suspend",
  "admin.reinstate": "Reinstate",
  "admin.confirmSuspend": "Suspend this account? The person will lose access until reinstated.",
  "admin.confirmReinstate": "Reinstate this account (set status back to active)?",
  "admin.adminsOnly": "Admins only — this page shows the moderation queue for site administrators.",

  "viewOnMap.button": "View on map",

  // Report button / dialog
  "report.button": "Report this content",
  "report.dialogAria": "Report content",
  "report.heading": "Report this content",
  "report.thanks": "Thanks, we'll take a look.",
  "report.intro": "Tell us what's wrong — a moderator will review it.",
  "report.placeholder": "What's the problem? (at least 10 characters)",
  "report.minChars": "{count}/{min} characters minimum",
  "report.chars": "{count}/{max} characters",
  "report.busy": "Reporting…",
  "report.submit": "Report",

  // Track export
  "export.busy": "Exporting…",

  // Elevation profile chart
  "chart.axisAria": "Profile X axis",
  "chart.aria": "Elevation profile: {distance}, from {min} to {max}",
  "chart.min": "Min",
  "chart.max": "Max",

  // Pickers
  "picker.searchPeople": "Search people by name…",
  "picker.noPeople": "No people found.",
  "picker.removePersonAria": "Remove {name}",
  "picker.searchPlaces": "Search places by name…",
  "picker.linkAnotherPlace": "Link another place…",
  "picker.noPlaces": "No places found.",
  "picker.removePlaceAria": "Remove place {name}",
  "picker.searchRoutes": "Search routes by name…",
  "picker.addAnotherRoute": "Add another route…",
  "picker.noRoutes": "No routes found.",
  "picker.removeRouteAria": "Remove route {name}",
  "picker.styleAria": "Style",
  "picker.stylePlaceholder": "Style…",
  "picker.resultAria": "Result",
  "picker.resultPlaceholder": "Result (e.g. Summit)",

  // Stars
  "stars.one": "{count} star",
  "stars.other": "{count} stars",

  // Error boundary
  "error.somethingWrong": "Something went wrong.",
  "error.reload": "Reload",
} as const;

export type MessageKey = keyof typeof en;

export const de = {
  "nav.map": "Karte",
  "nav.feed": "Feed",
  "nav.discover": "Entdecken",
  "nav.calendar": "Kalender",
  "nav.new": "Neu",
  "nav.ticklist": "Ticklist",
  "nav.saved": "Gespeichert",
  "nav.profile": "Profil",
  "nav.moderation": "Moderation",
  "nav.myProfile": "Mein Profil",
  "nav.notFound": "Seite nicht gefunden.",

  "theme.system": "System-Theme",
  "theme.light": "Helles Theme",
  "theme.dark": "Dunkles Theme",
  "theme.toggleTitle": "{current} — klicken für {next}",
  "theme.toggleAria": "Theme: {current}. Wechseln zu {next}",

  "locale.label": "Sprache",

  "common.loading": "Lädt…",
  "common.save": "Speichern",
  "common.saving": "Speichert…",
  "common.cancel": "Abbrechen",
  "common.close": "Schließen",
  "common.delete": "Löschen",
  "common.deleting": "Löscht…",
  "common.remove": "Entfernen",
  "common.undo": "Rückgängig",
  "common.done": "Erledigt",
  "common.sort": "Sortieren",
  "common.name": "Name",
  "common.type": "Typ",
  "common.any": "Alle",
  "common.notSpecified": "Keine Angabe",
  "common.logIn": "Anmelden",
  "common.register": "Registrieren",
  "common.someone": "Jemand",
  "common.unnamed": "Namenloser Abenteurer",
  "common.by": "von",

  "enum.HIKING": "Wandern",
  "enum.TRAIL_RUNNING": "Trailrunning",
  "enum.SNOWSHOEING": "Schneeschuhwandern",
  "enum.SKI_TOURING": "Skitouren",
  "enum.MOUNTAINEERING": "Bergsteigen",
  "enum.ROCK_CLIMBING": "Klettern",
  "enum.ICE_CLIMBING": "Eisklettern",
  "enum.VIA_FERRATA": "Klettersteig",
  "enum.SUMMIT": "Gipfel",
  "enum.HUT": "Hütte",
  "enum.PASS": "Pass",
  "enum.CRAG": "Klettergarten",
  "enum.VIEWPOINT": "Aussichtspunkt",
  "enum.PARKING": "Parkplatz",
  "enum.EXCELLENT": "Ausgezeichnet",
  "enum.GOOD": "Gut",
  "enum.MEDIOCRE": "Mittelmäßig",
  "enum.POOR": "Schlecht",
  "enum.NEXT_UP": "Als Nächstes",
  "enum.THIS_SEASON": "Diese Saison",
  "enum.SOMEDAY": "Irgendwann",
  "enum.ONSIGHT": "Onsight",
  "enum.FLASH": "Flash",
  "enum.REDPOINT": "Rotpunkt",
  "enum.ATTEMPT": "Versuch",
  "enum.PUBLIC": "Öffentlich",
  "enum.FOLLOWERS": "Follower",
  "enum.PRIVATE": "Privat",
  "enum.OWNER": "Besitzer",
  "enum.PARTICIPANT": "Teilnehmer",
  "enum.OPEN": "Offen",
  "enum.RESOLVED": "Erledigt",
  "enum.DISMISSED": "Verworfen",
  "enum.ACTIVE": "Aktiv",
  "enum.SUSPENDED": "Gesperrt",
  "enum.BANNED": "Gebannt",
  "enum.DELETED": "Gelöscht",
  "enum.PLACE": "Ort",
  "enum.REGION": "Region",
  "enum.ROUTE": "Route",
  "enum.OUTING": "Tour",
  "enum.PERSON": "Person",
  "enum.COMMENT": "Kommentar",
  "enum.MAIN": "Hauptlinie",
  "enum.START": "Start",
  "enum.HIDDEN": "Versteckt",
  "enum.REQUEST_TO_JOIN": "Auf Anfrage",
  "enum.INVITE_ONLY": "Nur auf Einladung",
  "enum.ADMIN": "Admin",
  "enum.MEMBER": "Mitglied",
  "enum.PENDING": "Ausstehend",
  "enum.INVITED": "Eingeladen",
  "enum.MEMBERS": "Mitglieder",
  "enum.MEMBERS_ONLY": "Nur Mitglieder",
  "enum.CLOSED": "Geschlossen",
  "enum.ORGANIZER": "Organisator",
  "enum.ATTENDEE": "Teilnehmer",
  "enum.CONFIRMED": "Zugesagt",
  "enum.DECLINED": "Abgesagt",
  "enum.NONE": "Keine",
  "enum.DAILY": "Täglich",
  "enum.WEEKLY": "Wöchentlich",
  "enum.BIWEEKLY": "Alle zwei Wochen",
  "enum.MONTHLY": "Monatlich",
  "enum.PLANNED": "Geplant",
  "enum.COMPLETED": "Abgeschlossen",

  "stat.conditions": "Verhältnisse",
  "stat.time": "Zeit",
  "stat.distance": "Distanz",
  "stat.ascent": "Aufstieg",
  "stat.descent": "Abstieg",
  "stat.movingTime": "Zeit in Bewegung",
  "stat.pace": "Pace",
  "stat.duration": "Dauer",
  "stat.elevation": "Höhe",
  "stat.minElevation": "Tiefster Punkt",
  "stat.maxElevation": "Höchster Punkt",
  "stat.grade": "Schwierigkeit ({activity})",

  "tabs.info": "Info",
  "tabs.map": "Karte",
  "tabs.photos": "Fotos",
  "tabs.places": "Orte",
  "tabs.routes": "Routen",
  "tabs.outings": "Touren",
  "tabs.comments": "Kommentare",
  "tabs.subregions": "Teilregionen",
  "tabs.profile": "Profil",
  "tabs.followers": "Follower",
  "tabs.following": "Folgt",
  "tabs.ticklist": "Ticklist",
  "tabs.members": "Mitglieder",
  "tabs.events": "Veranstaltungen",
  "tabs.participants": "Teilnehmer",

  "gate.needAccount": "Du brauchst ein Konto, um {action}. Stöbern ist für alle offen — zum Erstellen ist eine Anmeldung nötig.",
  "gateAction.logOuting": "eine Tour einzutragen",
  "gateAction.keepTicklist": "eine Ticklist zu führen",
  "gateAction.keepSaved": "Orte auf der Karte zu speichern",
  "gateAction.seeProfile": "dein Profil zu sehen",
  "gateAction.seeCalendar": "deinen Kalender zu sehen",
  "gateAction.addPlace": "einen Ort hinzuzufügen",
  "gateAction.drawRoute": "eine Route zu zeichnen",
  "gateAction.editOuting": "eine Tour zu bearbeiten",
  "gateAction.createGroup": "eine Gruppe zu erstellen",
  "gateAction.editGroup": "eine Gruppe zu bearbeiten",
  "gateAction.createEvent": "eine Veranstaltung zu planen",
  "gateAction.editEvent": "eine Veranstaltung zu bearbeiten",

  "auth.email": "E-Mail",
  "auth.password": "Passwort",
  "login.title": "Anmelden",
  "login.busy": "Meldet an…",
  "login.failed": "Anmeldung fehlgeschlagen",
  "login.forgot": "Passwort vergessen?",
  "login.noAccount": "Noch kein Konto?",
  "login.demo": "Demo-Login: demo@gaea.dev / demo-password",
  "register.title": "Registrieren",
  "register.displayName": "Anzeigename",
  "register.passwordMin": "Passwort (mind. 8 Zeichen)",
  "register.tooShort": "Das Passwort muss mindestens 8 Zeichen haben.",
  "register.busy": "Erstellt Konto…",
  "register.submit": "Konto erstellen",
  "register.failed": "Registrierung fehlgeschlagen",
  "register.haveAccount": "Schon ein Konto?",
  "forgot.title": "Passwort zurücksetzen",
  "forgot.sent": "Falls zu dieser Adresse ein Konto existiert, wurde ein Link zum Zurücksetzen geschickt.",
  "forgot.devNote": "In der Entwicklung wird der Link nicht gemailt — er erscheint in den Logs des Auth-Dienstes.",
  "forgot.intro": "Gib deine Konto-E-Mail ein und wir schicken dir einen Link zum Zurücksetzen des Passworts.",
  "forgot.busy": "Sendet…",
  "forgot.submit": "Link senden",
  "forgot.remembered": "Doch wieder eingefallen?",
  "forgot.failed": "Anfrage fehlgeschlagen",
  "reset.title": "Neues Passwort wählen",
  "reset.invalid": "Dieser Link ist ungültig oder abgelaufen.",
  "reset.requestNew": "Neuen Link anfordern",
  "reset.newPassword": "Neues Passwort (mind. 8 Zeichen)",
  "reset.confirmPassword": "Neues Passwort bestätigen",
  "reset.mismatch": "Die Passwörter stimmen nicht überein.",
  "reset.busy": "Setzt zurück…",
  "reset.submit": "Passwort zurücksetzen",
  "reset.failed": "Zurücksetzen fehlgeschlagen",
  "verify.title": "E-Mail bestätigen",
  "verify.invalid": "Dieser Bestätigungslink ist ungültig oder abgelaufen.",
  "verify.requestNewPre": "Einen neuen kannst du anfordern über",
  "verify.yourProfile": "dein Profil",
  "verify.done": "Deine E-Mail ist bestätigt. Danke!",
  "verify.goProfile": "Zu deinem Profil",
  "verify.intro": "Klicke unten, um diese E-Mail-Adresse für dein Konto zu bestätigen.",
  "verify.busy": "Bestätigt…",
  "verify.submit": "Meine E-Mail bestätigen",
  "verify.failed": "Bestätigung fehlgeschlagen",

  "feed.title": "Aktuelle Touren",
  "feed.everyone": "Alle",
  "feed.following": "Gefolgt",
  "feed.since": "Seit",
  "feed.minRating": "Mind. Bewertung",
  "feed.sortNew": "Neueste",
  "feed.sortOld": "Älteste",
  "feed.sortTop": "Beste Bewertung",
  "feed.updating": "Aktualisiert…",
  "feed.loginToSeeFollowing": "um Touren von Personen zu sehen, denen du folgst.",
  "feed.notFollowingAnyone": "Du folgst noch niemandem. Öffne eine Tour und besuche das Profil des Autors, um ihm zu folgen.",
  "feed.emptyFollowing": "Keine Touren von Personen, denen du folgst, passen zu diesen Filtern.",
  "feed.emptyAll": "Keine Touren passen zu diesen Filtern.",
  "feed.loadingMore": "Lädt mehr…",
  "feed.endOne": "Das war's — {count} Tour.",
  "feed.endOther": "Das war's — {count} Touren.",

  "card.privateTitle": "Privat — nur du kannst diese Tour sehen",
  "card.privateAria": "Private Tour",
  "card.followersTitle": "Nur für Follower des Besitzers sichtbar",
  "card.followersAria": "Tour nur für Follower",
  "card.plannedTitle": "Geplante Tour — nur für dich sichtbar",
  "card.moreRoutes": "+{count} weitere",
  "card.conditions": "Verhältnisse: {value}",
  "card.commentsOne": "{count} Kommentar",
  "card.commentsOther": "{count} Kommentare",

  "like.ariaLike": "Diese Tour liken ({count} Likes)",
  "like.ariaUnlike": "Like für diese Tour entfernen ({count} Likes)",
  "like.ariaLogin": "Anmelden, um diese Tour zu liken ({count} Likes)",
  "follow.follow": "Folgen",
  "follow.following": "Gefolgt",

  "notif.title": "Benachrichtigungen",
  "notif.ariaCount": "Benachrichtigungen ({count} ungelesen)",
  "notif.markAll": "Alle als gelesen markieren",
  "notif.marking": "Markiert…",
  "notif.loadError": "Benachrichtigungen konnten nicht geladen werden.",
  "notif.empty": "Noch nichts — Likes, Kommentare und neue Follower erscheinen hier.",
  "notif.OUTING_LIKE": "gefällt deine Tour",
  "notif.OUTING_COMMENT": "hat deine Tour kommentiert",
  "notif.PERSON_FOLLOW": "folgt dir",
  "notif.CLUB_INVITE": "hat dich in eine Gruppe eingeladen",
  "notif.CLUB_JOIN_REQUEST": "möchte deiner Gruppe beitreten",
  "notif.CLUB_JOIN_APPROVED": "hat deine Beitrittsanfrage angenommen",
  "notif.EVENT_INVITE": "hat dich zu einer Veranstaltung eingeladen",

  "search.placeholder": "Orte, Routen, Personen suchen…",
  "search.sectionPlaces": "Orte",
  "search.sectionRegions": "Regionen",
  "search.sectionRoutes": "Routen",
  "search.sectionPeople": "Personen",
  "search.sectionOutings": "Touren",
  "search.nearbyPlaces": "Orte in der Nähe",
  "search.nearbyRoutes": "Routen in der Nähe",
  "search.nearMapCenter": "Nahe Kartenmitte",
  "search.nearMe": "In meiner Nähe",
  "search.locating": "Ortet…",
  "search.geoError": "Standort nicht verfügbar — erlaube den Standortzugriff und versuche es erneut.",
  "search.findingNearby": "Sucht in der Nähe…",
  "search.nearbyError": "Ergebnisse in der Nähe konnten nicht geladen werden.",
  "search.nothingNearby": "Nichts in der Nähe.",
  "search.searching": "Sucht…",
  "search.noMatches": "Keine Treffer.",
  "search.showOnMap": "Auf der Karte zeigen",
  "search.person": "Person",
  "search.unnamed": "Ohne Namen",
  "search.away": "{distance} entfernt",

  "map.addPlaceHere": "Hier einen Ort hinzufügen?",
  "map.add": "Hinzufügen",
  "map.planEventHere": "Hier eine Veranstaltung planen",
  "map.saveSpotHere": "Diesen Punkt speichern",
  "map.spotNamePlaceholder": "z. B. Geheimer Aussichtspunkt",
  "map.savedPlaces": "Gespeichert",
  "map.savedEmpty": "Noch nichts gespeichert — nutze „Speichern“ auf einer Ortsseite oder klicke mit rechts auf die Karte.",
  "map.manageSaved": "Alle gespeicherten Orte",
  "map.filters": "Filter",
  "map.drawRoute": "Route zeichnen",
  "map.zoomForData": "Zoome hinein, um Kartendaten zu sehen",
  "map.zoomForRoutes": "Zoome hinein, um Routen zu sehen",
  "map.truncated": "{shown} von {total} {layer}",
  "map.truncatedMore": " (+{count} weitere)",
  "map.truncatedZoom": " — zoome hinein für den Rest",
  "map.loadError": "Kartendaten konnten nicht geladen werden",
  "map.layerPlaces": "Orten",
  "map.layerRoutes": "Routen",
  "map.layerRegions": "Regionen",
  "map.layerOutings": "Touren",
  "map.layerEvents": "Veranstaltungen",

  "filters.title": "Anzeige & Filter",
  "filters.closeAria": "Filter schließen",
  "filters.showOnMap": "Auf der Karte zeigen",
  "filters.outings": "Touren",
  "filters.routes": "Routen",
  "filters.routeTracks": "Tracks",
  "filters.regions": "Regionen",
  "filters.events": "Veranstaltungen",
  "filters.tracksHint": "Tracks erscheinen ab Zoom 11 — zoome hinein, wenn du sie nicht siehst.",
  "filters.clustering": "Nahe Pins bündeln",
  "filters.routeActivities": "Routen-Aktivitäten",
  "filters.placeTypes": "Ortstypen",
  "filters.elevMin": "Ortshöhe ≥ (m)",
  "filters.elevMax": "Ortshöhe ≤ (m)",
  "filters.outingsFrom": "Touren ab",
  "filters.outingsUntil": "Touren bis",
  "filters.distMin": "Routendistanz ≥ (km)",
  "filters.distMax": "Routendistanz ≤ (km)",
  "filters.ascentMin": "Routenaufstieg ≥ (m)",
  "filters.nameContains": "Routenname enthält",
  "filters.clear": "Filter zurücksetzen",

  "tooltip.clickForDetails": "Für Details klicken",

  "profileMap.title": "Aktivitätskarte",
  "profileMap.outingsOne": "{count} Tour",
  "profileMap.outingsOther": "{count} Touren",
  "profileMap.w30d": "30 Tage",
  "profileMap.w3m": "3 Monate",
  "profileMap.w12m": "12 Monate",
  "profileMap.wAll": "Gesamt",
  "profileMap.empty": "Keine Touren mit Karte in diesem Zeitraum.",

  "profile.loadError": "Dein Profil konnte nicht geladen werden.",
  "profile.editName": "Anzeigename bearbeiten",
  "profile.publicProfile": "Öffentliches Profil",
  "profile.logout": "Abmelden",
  "profile.myOutings": "Meine Touren",
  "profile.noOutings": "Noch keine Touren — trage deine erste über eine Routenseite oder den Tab „Neu“ ein.",
  "profile.joinedAs": "Als Teilnehmer dabei",
  "profile.changePassword": "Passwort ändern",
  "avatar.change": "Foto ändern",
  "avatar.upload": "Foto hochladen",
  "avatar.uploadFailed": "Upload fehlgeschlagen",
  "email.verified": "bestätigt",
  "email.notVerified": "E-Mail nicht bestätigt",
  "email.sentNote": "Gesendet — schau in dein Postfach. (In der Entwicklung wird der Link nicht gemailt — er erscheint in den Logs des Auth-Dienstes.)",
  "email.send": "Bestätigungsmail senden",
  "email.sending": "Sendet…",
  "email.sendFailed": "E-Mail konnte nicht gesendet werden",
  "pw.current": "Aktuelles Passwort",
  "pw.new": "Neues Passwort (mind. 8 Zeichen)",
  "pw.confirm": "Neues Passwort bestätigen",
  "pw.tooShort": "Das neue Passwort muss mindestens 8 Zeichen haben.",
  "pw.mismatch": "Die neuen Passwörter stimmen nicht überein.",
  "pw.wrongCurrent": "Das aktuelle Passwort ist falsch.",
  "pw.failed": "Passwortänderung fehlgeschlagen",
  "pw.changed": "Passwort geändert.",
  "pw.changing": "Ändert…",
  "pw.submit": "Passwort ändern",

  "person.notFound": "Person nicht gefunden.",
  "person.joined": "Dabei seit {date}",
  "person.activities": "Aktivitäten",
  "person.noneYetSelf": "Noch keine — wähle deine Aktivitäten in deinem Profil.",
  "person.noneListed": "Keine angegeben.",
  "person.noOutings": "Noch keine Touren eingetragen.",
  "person.noFollowers": "Noch keine Follower.",
  "person.notFollowingSelf": "Du folgst noch niemandem.",
  "person.notFollowing": "Folgt noch niemandem.",

  "ticklist.title": "Ticklist",
  "ticklist.intro": "Routen, die du machen willst. Öffentlich markierte Ticks erscheinen auf deinem Profil — alles andere siehst nur du.",
  "ticklist.empty": "Noch nichts auf deiner Ticklist — öffne eine Route und klicke auf „Ticklist“.",
  "ticklist.browseMap": "Karte durchstöbern",
  "ticklist.done": "Erledigt",
  "ticklist.doneOn": "Erledigt am {date}",
  "ticklist.log": "Eintragen",

  "savePlace.button": "Speichern",
  "savePlace.saved": "Gespeichert",
  "savePlace.buttonTitle": "Diesen Ort für schnellen Zugriff auf der Karte speichern",
  "savePlace.savedTitle": "Aus deinen gespeicherten Orten entfernen",
  "saved.title": "Gespeicherte Orte",
  "saved.intro": "Orte und Kartenpunkte für schnellen Zugriff. Nur du kannst sie sehen.",
  "saved.tabPlaces": "Orte",
  "saved.tabActivity": "Aktivität in der Nähe",
  "saved.empty": "Noch nichts gespeichert — klicke „Speichern“ auf einer Ortsseite oder mit rechts auf die Karte.",
  "saved.browseMap": "Karte durchstöbern",
  "saved.customPin": "Eigener Punkt",
  "saved.showOnMap": "Karte",
  "saved.showOnMapTitle": "Auf der Karte zeigen",
  "saved.savedOn": "Gespeichert am {date}",
  "saved.allSaved": "Alle",
  "saved.nearbyNote": "Aktuelle Touren im Umkreis von ~10 km deiner gespeicherten Orte.",
  "saved.emptyActivity": "Noch keine Touren in der Nähe deiner gespeicherten Orte.",
  "saved.renameTitle": "Umbenennen",
  "saved.removeTitle": "Aus Gespeichert entfernen",
  "saved.latest": "Zeigt die neuesten {count} Touren.",
  "tick.priorityAria": "Priorität",
  "tick.publicTitle": "Öffentlich — auf deinem Profil sichtbar. Klicken, um privat zu machen.",
  "tick.privateTitle": "Privat — nur du siehst ihn. Klicken, um ihn auf deinem Profil zu zeigen.",
  "tick.makePrivate": "Tick privat machen",
  "tick.makePublic": "Tick öffentlich machen",
  "tick.removeTitle": "Von der Ticklist entfernen",

  "tick.button": "Ticklist",
  "tick.priority": "Priorität",
  "tick.targetSeason": "Ziel-Saison",
  "tick.seasonPlaceholder": "Sommer 2027",
  "tick.note": "Private Notiz",
  "tick.notePlaceholder": "Partner, Ausrüstung, Verhältnisse zum Abwarten…",
  "tick.add": "Zur Ticklist hinzufügen",

  "place.notFound": "Ort nicht gefunden.",
  "place.addedBy": "Hinzugefügt von",
  "place.editPlace": "Ort bearbeiten",
  "place.confirmDelete": "Diesen Ort löschen? Das kann nicht rückgängig gemacht werden.",
  "place.elevationOptional": "Höhe (m, optional)",
  "place.sortLinked": "Verknüpfte zuerst",
  "place.sortDistance": "Distanz (kürzeste)",
  "place.sortAscent": "Aufstieg (meiste)",
  "place.linked": "verknüpft",
  "place.passes": "führt {distance} entfernt vorbei",
  "place.noRoutes": "Noch keine Routen an diesem Ort.",

  "newPlace.heading": "Neuer Ort",
  "newPlace.title": "Ort hinzufügen",
  "newPlace.pickPre": "Wähle zuerst eine Stelle — rechtsklicke (oder drücke lange) auf die",
  "newPlace.pickMap": "Karte",
  "newPlace.pickPost": "wo der Ort hingehört.",
  "newPlace.namePlaceholder": "z. B. Sněžka",
  "newPlace.busy": "Fügt hinzu…",
  "newPlace.submit": "Ort hinzufügen",

  "region.kicker": "Region",
  "region.notFound": "Region nicht gefunden.",
  "region.partOf": "Teil von",
  "region.noSubregions": "Keine Teilregionen.",
  "region.sortElevation": "Höhe (höchste)",
  "region.noPlaces": "Noch keine Orte in dieser Region erfasst (die Zugehörigkeit wird aus der Geometrie berechnet).",

  "route.kicker": "Route",
  "route.notFound": "Route nicht gefunden.",
  "route.editDetails": "Details bearbeiten",
  "route.logOuting": "Tour eintragen",
  "route.ratingsOne": "({count} Bewertung)",
  "route.ratingsOther": "({count} Bewertungen)",
  "route.wantOne": "{count} Person will diese Route machen",
  "route.wantOther": "{count} Personen wollen diese Route machen",
  "route.addedBy": "Hinzugefügt von",
  "route.aMember": "einem Mitglied",
  "route.related": "Verwandte Routen",
  "route.noPlaces": "Noch keine Orte mit dieser Route verknüpft.",
  "route.photoSample": "Auswahl aus aktuellen Touren — öffne eine Tour für alle ihre Fotos.",
  "route.noPhotos": "Noch keine Fotos — Fotos aus Touren auf dieser Route erscheinen hier.",
  "route.noOutings": "Noch keine Touren auf dieser Route.",
  "route.beFirst": "Sei die erste!",
  "route.confirmDelete": "Route „{name}“ löschen? Das kann nicht rückgängig gemacht werden.",
  "route.fkDelete": "Diese Route wird von Touren referenziert und kann nicht gelöscht werden.",

  "outing.kicker": "Tour",
  "outing.notFound": "Tour nicht gefunden.",
  "outing.confirmDelete": "Diese Tour löschen? Das kann nicht rückgängig gemacht werden.",
  "outing.privateTitle": "Nur du kannst diese Tour sehen",
  "outing.followersTitle": "Nur für Follower des Besitzers sichtbar",
  "outing.plannedTitle": "Geplant — nur für dich sichtbar, bis du sie als abgeschlossen markierst",
  "outing.markCompleted": "Als abgeschlossen markieren",
  "outing.party": "Gruppe",
  "outing.noGeometry": "Kein Track und keine Routengeometrie für diese Tour.",
  "outing.noPhotos": "Keine Fotos für diese Tour.",
  "outing.noRoutes": "Keine Routen mit dieser Tour verknüpft.",
  "outing.edit": "Bearbeiten",

  "comments.back": "← Zurück zu allen Kommentaren",
  "comments.replyingUnder": "Antwort unter",
  "comments.deleted": "[gelöscht]",
  "comments.empty": "Noch keine Kommentare. Fang die Unterhaltung an.",
  "comments.author": "Autor",
  "comments.wasDeleted": "Dieser Kommentar wurde gelöscht.",
  "comments.reply": "Antworten",
  "comments.confirmDelete": "Diesen Kommentar löschen? Antworten darauf bleiben sichtbar.",
  "comments.continueOne": "Diesen Thread fortsetzen ({count} Antwort)",
  "comments.continueOther": "Diesen Thread fortsetzen ({count} Antworten)",
  "comments.replyPlaceholder": "Antwort schreiben…",
  "comments.addPlaceholder": "Kommentar hinzufügen…",
  "comments.posting": "Sendet…",
  "comments.submit": "Kommentieren",
  "comments.loginToJoin": "um mitzudiskutieren.",

  "form.title": "Titel",
  "form.name": "Name",
  "form.date": "Datum",
  "form.activity": "Aktivität",
  "form.activities": "Aktivitäten",
  "form.conditions": "Verhältnisse",
  "form.rating": "Bewertung",
  "form.notes": "Notizen",
  "form.description": "Beschreibung",
  "form.visibility": "Sichtbarkeit",
  "form.status": "Status",
  "form.durationOptional": "Dauer (Minuten, optional)",
  "form.pickActivity": "Wähle mindestens eine Aktivität.",

  "newOuting.title": "Tour eintragen",
  "newOuting.titlePlaceholder": "z. B. Sněžka-Runde zum Sonnenaufgang",
  "newOuting.routesOptional": "Routen (optional)",
  "newOuting.gpxOptional": "GPX-Track (optional)",
  "newOuting.timeHours": "Zeit (Stunden)",
  "newOuting.notesPlaceholder": "Verhältnisse, Highlights, Warnungen für andere…",
  "newOuting.photosOptional": "Fotos (optional)",
  "newOuting.participantsOptional": "Teilnehmer (optional)",
  "newOuting.followersHint": "Follower: nur Personen, die dir folgen.",
  "newOuting.plannedHint": "Eine geplante Tour ist nur für dich sichtbar, bis du sie als abgeschlossen markierst.",
  "newOuting.ownerNote": "Du wirst als Besitzer eingetragen.",
  "newOuting.submit": "Tour speichern",
  "newOuting.errTitle": "Gib deiner Tour einen Titel.",
  "newOuting.errDate": "Wann warst du unterwegs?",
  "newOuting.errFuture": "Eine abgeschlossene Tour kann nicht in der Zukunft liegen — markiere sie stattdessen als geplant.",
  "newOuting.errElapsed": "Die Zeit muss eine positive Stundenzahl sein.",
  "err.owner": "Beim Eintragen als Besitzer dieser Tour ging etwas schief. Bitte versuche es erneut.",
  "err.permission": "Dazu fehlt dir die Berechtigung — versuche, dich neu anzumelden.",
  "err.rejected": "Der Server hat die Daten abgelehnt: {msg}",
  "gpx.stats": "{count} Punkte · ~{distance}",
  "gpx.removeAria": "GPX-Datei entfernen",
  "edit.title": "Tour bearbeiten",
  "edit.notOwner": "Nur der Besitzer dieser Tour kann sie bearbeiten.",
  "edit.noPermission": "Du darfst diese Tour nicht bearbeiten.",
  "edit.gpx": "GPX-Track",
  "edit.replacesOnSave": "ersetzt beim Speichern den aktuellen Track",
  "edit.cancelTrackAria": "Track-Änderung abbrechen",
  "edit.trackRemoved": "Der Track wird beim Speichern entfernt.",
  "edit.trackAttached": "Track vorhanden · {distance} / {ascent} ↑",
  "edit.replace": "Ersetzen",
  "edit.photos": "Fotos",
  "edit.routes": "Routen",
  "edit.participants": "Teilnehmer",
  "edit.submit": "Änderungen speichern",
  "photo.removedUndo": "Entfernt — rückgängig",
  "photo.removeAria": "Foto entfernen",
  "photo.caption": "Bildunterschrift (optional)",
  "photo.add": "Fotos hinzufügen",
  "photo.addMore": "Mehr Fotos hinzufügen",
  "photo.closeAria": "Foto schließen",
  "photo.label": "Foto",

  "newRoute.heading": "Neue Route",
  "newRoute.title": "Route zeichnen",
  "newRoute.drawTab": "Auf der Karte zeichnen",
  "newRoute.uploadTab": "GPX hochladen",
  "newRoute.gpxFile": "GPX-Datei",
  "newRoute.pointsOne": "{count} Punkt",
  "newRoute.pointsOther": "{count} Punkte",
  "newRoute.undo": "Rückgängig",
  "newRoute.undoTitle": "Letzten Punkt zurücknehmen (Strg+Z)",
  "newRoute.clear": "Leeren",
  "newRoute.drawHelp": "Klicken, um Punkte zu setzen · Punkt ziehen zum Verschieben · Punkt anklicken und Entf zum Löschen",
  "newRoute.uploadHint": "Lade eine GPX-Datei hoch, um sie hier zu sehen.",
  "newRoute.noElevNote": "Gezeichnete Routen haben keine Höhendaten — der Aufstieg erscheint als 0. Höhendaten kommen aus GPX-Uploads.",
  "newRoute.namePlaceholder": "z. B. Sněžka über Obří důl",
  "newRoute.linkedPlaces": "Verknüpfte Orte (optional)",
  "newRoute.errName": "Gib der Route einen Namen.",
  "newRoute.errGeomDraw": "Zeichne mindestens zwei Punkte auf der Karte.",
  "newRoute.errGeomUpload": "Lade zuerst eine GPX-Datei hoch.",
  "newRoute.errDuration": "Die Dauer muss eine positive Minutenzahl sein.",
  "newRoute.busy": "Erstellt…",
  "newRoute.submit": "Route erstellen",

  "group.kicker": "Gruppe",
  "group.notFound": "Gruppe nicht gefunden.",
  "group.membersOne": "{count} Mitglied",
  "group.membersOther": "{count} Mitglieder",
  "group.created": "Erstellt am {date}",
  "group.joinPolicy": "Beitrittsregel",
  "group.join": "Gruppe beitreten",
  "group.requestJoin": "Beitritt anfragen",
  "group.requestPending": "Anfrage ausstehend",
  "group.withdrawRequest": "Anfrage zurückziehen",
  "group.inviteOnlyNote": "Nur auf Einladung — ein Admin muss dich einladen.",
  "group.invitedBanner": "Du wurdest in diese Gruppe eingeladen.",
  "group.acceptInvite": "Einladung annehmen",
  "group.decline": "Ablehnen",
  "group.leave": "Gruppe verlassen",
  "group.confirmLeave": "Diese Gruppe verlassen?",
  "group.bannedNote": "Du wurdest aus dieser Gruppe verbannt.",
  "group.edit": "Gruppe bearbeiten",
  "group.confirmDelete": "Diese Gruppe löschen? Das kann nicht rückgängig gemacht werden.",
  "group.noDescription": "Noch keine Beschreibung.",
  "group.noOutings": "Noch keine Touren in dieser Gruppe geteilt.",
  "group.membersHidden": "Die Mitgliederliste ist nur für Mitglieder sichtbar.",
  "group.eventsSoon": "Gruppen-Veranstaltungen kommen bald.",
  "group.pendingRequests": "Ausstehende Anfragen",
  "group.approve": "Annehmen",
  "group.promote": "Zum Admin machen",
  "group.demote": "Admin-Rolle entfernen",
  "group.ban": "Verbannen",
  "group.unban": "Entbannen",
  "group.confirmBan": "{name} aus dieser Gruppe verbannen?",
  "group.confirmKick": "{name} aus dieser Gruppe entfernen?",
  "group.invite": "Personen einladen",
  "group.newEvent": "Neue Veranstaltung",
  "group.noUpcomingEvents": "Keine bevorstehenden Veranstaltungen.",

  "newGroup.title": "Gruppe erstellen",
  "newGroup.namePlaceholder": "z. B. Alpine Wanderers",
  "newGroup.errName": "Gib der Gruppe einen Namen.",
  "newGroup.visibilityHint": "Versteckt: nur Mitglieder können die Gruppe sehen.",
  "newGroup.ownerNote": "Du wirst als Besitzer der Gruppe eingetragen.",
  "newGroup.submit": "Gruppe erstellen",
  "editGroup.adminsOnly": "Nur Gruppen-Admins können diese Gruppe bearbeiten.",

  "share.label": "In Gruppe teilen",
  "share.select": "Gruppe wählen…",
  "share.removeAria": "Nicht mehr mit {name} teilen",
  "share.button": "Teilen",
  "share.copied": "Link kopiert",

  "event.kicker": "Veranstaltung",
  "event.notFound": "Veranstaltung nicht gefunden.",
  "event.invitedBanner": "Du wurdest zu dieser Veranstaltung eingeladen.",
  "event.acceptInvite": "Einladung annehmen",
  "event.join": "Teilnehmen",
  "event.requestJoin": "Teilnahme anfragen",
  "event.requestPending": "Anfrage ausstehend",
  "event.withdrawRequest": "Anfrage zurückziehen",
  "event.youAreGoing": "Du nimmst teil",
  "event.cantGo": "Absagen",
  "event.youDeclined": "Du hast abgesagt",
  "event.imGoing": "Ich komme",
  "event.membersOnlyPre": "Nur Mitglieder von",
  "event.theGroup": "der Gruppe",
  "event.membersOnlyPost": "können an dieser Veranstaltung teilnehmen.",
  "event.inviteOnlyNote": "Nur auf Einladung — ein Organisator muss dich einladen.",
  "event.closedNote": "Die Teilnahme wird von den Organisatoren verwaltet.",
  "event.addToCalendar": "Zum Kalender hinzufügen",
  "event.edit": "Bearbeiten",
  "event.confirmDelete": "Diese Veranstaltung löschen? Das kann nicht rückgängig gemacht werden.",
  "event.repeats": "Wiederholung: {freq}",
  "event.repeatsUntil": "Wiederholung: {freq} (bis {date})",
  "event.organizedBy": "Organisiert von",
  "event.goingCount": "{count} Zusagen",
  "event.goingCap": "{count} / {cap} Zusagen",
  "event.fullHint": "Voll",
  "event.noDescription": "Noch keine Beschreibung.",
  "event.maxParticipants": "Max. Teilnehmer",
  "event.recurrence": "Wiederholung",
  "event.meetingPoint": "Treffpunkt",
  "event.noRoutes": "Noch keine Routen für diese Veranstaltung geplant.",
  "event.noGeometry": "Kein Ort und keine Routengeometrie für diese Veranstaltung.",
  "event.organizers": "Organisatoren",
  "event.goingSection": "Zusagen",
  "event.nobodyYet": "Noch keine Zusagen.",
  "event.confirmRemove": "{name} von dieser Veranstaltung entfernen?",

  "calendar.today": "Heute",
  "calendar.prevMonth": "Vormonat",
  "calendar.nextMonth": "Nächster Monat",
  "calendar.legendOwn": "Deine Touren",
  "calendar.legendFollowed": "Personen, denen du folgst",
  "calendar.legendEvent": "Veranstaltungen",
  "calendar.more": "+{count} weitere",
  "calendar.dayEmpty": "Nichts an diesem Tag.",
  "calendar.monthEmpty": "Diesen Monat noch nichts.",

  "discover.title": "Entdecken",
  "discover.groups": "Gruppen",
  "discover.events": "Veranstaltungen",
  "discover.searchGroups": "Gruppen suchen…",
  "discover.searchEvents": "Veranstaltungen suchen…",
  "discover.newGroup": "Neue Gruppe",
  "discover.newEvent": "Neue Veranstaltung",
  "discover.yourGroups": "Deine Gruppen",
  "discover.yourEvents": "Deine Veranstaltungen",
  "discover.invited": "Eingeladen",
  "discover.hiddenGroup": "Verborgene Gruppe",
  "discover.hiddenEvent": "Verborgene Veranstaltung",
  "discover.noGroups": "Keine Gruppen gefunden.",
  "discover.noEvents": "Keine bevorstehenden Veranstaltungen gefunden.",
  "discover.myGroupsOnly": "In meinen Gruppen",
  "discover.notInAnyGroup": "Du bist noch in keiner Gruppe.",

  "newEvent.title": "Veranstaltung planen",
  "newEvent.titlePlaceholder": "z. B. Sonnenaufgangswanderung zur Croix de Fer",
  "newEvent.descriptionPlaceholder": "Plan, Details zum Treffpunkt, was mitzubringen ist…",
  "newEvent.start": "Beginn",
  "newEvent.endOptional": "Ende (optional)",
  "newEvent.groupOptional": "Gruppe (optional)",
  "newEvent.noGroup": "Keine Gruppe — eigenständige Veranstaltung",
  "newEvent.groupHint": "Mit Gruppe stehen auch Sichtbarkeit und Beitritt „Nur Mitglieder“ zur Verfügung.",
  "newEvent.repeatsUntil": "Wiederholen bis",
  "newEvent.maxOptional": "Max. Teilnehmer (optional)",
  "newEvent.meetingPointOptional": "Treffpunkt (optional)",
  "newEvent.mapPoint": "Kartenpunkt: {lat}, {lng}",
  "newEvent.clearPointAria": "Kartenpunkt entfernen",
  "newEvent.showOnMap": "Auf der Karte zeigen",
  "newEvent.showOnMapHint": "Bevorstehende Veranstaltungen mit Treffpunkt erscheinen auf der Karte.",
  "newEvent.routesOptional": "Geplante Routen (optional)",
  "newEvent.organizerNote": "Du wirst als Organisator eingetragen.",
  "newEvent.submit": "Veranstaltung erstellen",
  "newEvent.errTitle": "Gib der Veranstaltung einen Titel.",
  "newEvent.errStart": "Wann geht es los?",
  "newEvent.errEnd": "Das Ende muss nach dem Beginn liegen.",
  "newEvent.errCap": "Max. Teilnehmer muss eine positive Zahl sein.",
  "editEvent.title": "Veranstaltung bearbeiten",
  "editEvent.notOrganizer": "Nur Organisatoren können diese Veranstaltung bearbeiten.",
  "editEvent.noPermission": "Du darfst diese Veranstaltung nicht bearbeiten.",

  "admin.title": "Moderationswarteschlange",
  "admin.reportsOne": "{count} Meldung",
  "admin.reportsOther": "{count} Meldungen",
  "admin.all": "Alle",
  "admin.accountUpdated": "Kontostatus aktualisiert.",
  "admin.loadingReports": "Lädt Meldungen…",
  "admin.noReports": "Keine passenden Meldungen.",
  "admin.view": "{kind} ansehen",
  "admin.reportedBy": "Gemeldet von",
  "admin.resolve": "Erledigen",
  "admin.dismiss": "Verwerfen",
  "admin.account": "Konto: {status}",
  "admin.suspend": "Sperren",
  "admin.reinstate": "Entsperren",
  "admin.confirmSuspend": "Dieses Konto sperren? Die Person verliert den Zugang, bis sie entsperrt wird.",
  "admin.confirmReinstate": "Dieses Konto entsperren (Status zurück auf aktiv)?",
  "admin.adminsOnly": "Nur für Admins — diese Seite zeigt die Moderationswarteschlange für Administratoren.",

  "viewOnMap.button": "Auf Karte anzeigen",

  "report.button": "Inhalt melden",
  "report.dialogAria": "Inhalt melden",
  "report.heading": "Diesen Inhalt melden",
  "report.thanks": "Danke, wir schauen es uns an.",
  "report.intro": "Sag uns, was nicht stimmt — ein Moderator prüft es.",
  "report.placeholder": "Was ist das Problem? (mindestens 10 Zeichen)",
  "report.minChars": "{count}/{min} Zeichen mindestens",
  "report.chars": "{count}/{max} Zeichen",
  "report.busy": "Meldet…",
  "report.submit": "Melden",

  "export.busy": "Exportiert…",

  "chart.axisAria": "X-Achse des Profils",
  "chart.aria": "Höhenprofil: {distance}, von {min} bis {max}",
  "chart.min": "Min",
  "chart.max": "Max",

  "picker.searchPeople": "Personen nach Name suchen…",
  "picker.noPeople": "Keine Personen gefunden.",
  "picker.removePersonAria": "{name} entfernen",
  "picker.searchPlaces": "Orte nach Name suchen…",
  "picker.linkAnotherPlace": "Weiteren Ort verknüpfen…",
  "picker.noPlaces": "Keine Orte gefunden.",
  "picker.removePlaceAria": "Ort {name} entfernen",
  "picker.searchRoutes": "Routen nach Name suchen…",
  "picker.addAnotherRoute": "Weitere Route hinzufügen…",
  "picker.noRoutes": "Keine Routen gefunden.",
  "picker.removeRouteAria": "Route {name} entfernen",
  "picker.styleAria": "Stil",
  "picker.stylePlaceholder": "Stil…",
  "picker.resultAria": "Ergebnis",
  "picker.resultPlaceholder": "Ergebnis (z. B. Gipfel)",

  "stars.one": "{count} Stern",
  "stars.other": "{count} Sterne",

  "error.somethingWrong": "Etwas ist schiefgelaufen.",
  "error.reload": "Neu laden",
} satisfies Record<MessageKey, string>;

export const fr = {
  "nav.map": "Carte",
  "nav.feed": "Fil",
  "nav.discover": "Découvrir",
  "nav.calendar": "Calendrier",
  "nav.new": "Nouveau",
  "nav.ticklist": "Projets",
  "nav.saved": "Enregistrés",
  "nav.profile": "Profil",
  "nav.moderation": "Modération",
  "nav.myProfile": "Mon profil",
  "nav.notFound": "Page introuvable.",

  "theme.system": "Thème système",
  "theme.light": "Thème clair",
  "theme.dark": "Thème sombre",
  "theme.toggleTitle": "{current} — cliquer pour {next}",
  "theme.toggleAria": "Thème : {current}. Passer à {next}",

  "locale.label": "Langue",

  "common.loading": "Chargement…",
  "common.save": "Enregistrer",
  "common.saving": "Enregistrement…",
  "common.cancel": "Annuler",
  "common.close": "Fermer",
  "common.delete": "Supprimer",
  "common.deleting": "Suppression…",
  "common.remove": "Retirer",
  "common.undo": "Annuler",
  "common.done": "Fait",
  "common.sort": "Trier",
  "common.name": "Nom",
  "common.type": "Type",
  "common.any": "Toutes",
  "common.notSpecified": "Non précisé",
  "common.logIn": "Se connecter",
  "common.register": "S'inscrire",
  "common.someone": "Quelqu'un",
  "common.unnamed": "Aventurier sans nom",
  "common.by": "par",

  "enum.HIKING": "Randonnée",
  "enum.TRAIL_RUNNING": "Trail",
  "enum.SNOWSHOEING": "Raquettes",
  "enum.SKI_TOURING": "Ski de randonnée",
  "enum.MOUNTAINEERING": "Alpinisme",
  "enum.ROCK_CLIMBING": "Escalade",
  "enum.ICE_CLIMBING": "Cascade de glace",
  "enum.VIA_FERRATA": "Via ferrata",
  "enum.SUMMIT": "Sommet",
  "enum.HUT": "Refuge",
  "enum.PASS": "Col",
  "enum.CRAG": "Falaise",
  "enum.VIEWPOINT": "Point de vue",
  "enum.PARKING": "Parking",
  "enum.EXCELLENT": "Excellentes",
  "enum.GOOD": "Bonnes",
  "enum.MEDIOCRE": "Moyennes",
  "enum.POOR": "Mauvaises",
  "enum.NEXT_UP": "Bientôt",
  "enum.THIS_SEASON": "Cette saison",
  "enum.SOMEDAY": "Un jour",
  "enum.ONSIGHT": "À vue",
  "enum.FLASH": "Flash",
  "enum.REDPOINT": "Après travail",
  "enum.ATTEMPT": "Essai",
  "enum.PUBLIC": "Publique",
  "enum.FOLLOWERS": "Abonnés",
  "enum.PRIVATE": "Privée",
  "enum.OWNER": "Propriétaire",
  "enum.PARTICIPANT": "Participant",
  "enum.OPEN": "Ouvert",
  "enum.RESOLVED": "Résolu",
  "enum.DISMISSED": "Rejeté",
  "enum.ACTIVE": "Actif",
  "enum.SUSPENDED": "Suspendu",
  "enum.BANNED": "Banni",
  "enum.DELETED": "Supprimé",
  "enum.PLACE": "Lieu",
  "enum.REGION": "Région",
  "enum.ROUTE": "Itinéraire",
  "enum.OUTING": "Sortie",
  "enum.PERSON": "Personne",
  "enum.COMMENT": "Commentaire",
  "enum.MAIN": "Tracé principal",
  "enum.START": "Départ",
  "enum.HIDDEN": "Masqué",
  "enum.REQUEST_TO_JOIN": "Sur demande",
  "enum.INVITE_ONLY": "Sur invitation",
  "enum.ADMIN": "Admin",
  "enum.MEMBER": "Membre",
  "enum.PENDING": "En attente",
  "enum.INVITED": "Invité",
  "enum.MEMBERS": "Membres",
  "enum.MEMBERS_ONLY": "Membres uniquement",
  "enum.CLOSED": "Fermé",
  "enum.ORGANIZER": "Organisateur",
  "enum.ATTENDEE": "Participant",
  "enum.CONFIRMED": "Confirmé",
  "enum.DECLINED": "Décliné",
  "enum.NONE": "Aucune",
  "enum.DAILY": "Quotidienne",
  "enum.WEEKLY": "Hebdomadaire",
  "enum.BIWEEKLY": "Toutes les deux semaines",
  "enum.MONTHLY": "Mensuelle",
  "enum.PLANNED": "Prévue",
  "enum.COMPLETED": "Terminée",

  "stat.conditions": "Conditions",
  "stat.time": "Temps",
  "stat.distance": "Distance",
  "stat.ascent": "Dénivelé +",
  "stat.descent": "Dénivelé −",
  "stat.movingTime": "Temps en mouvement",
  "stat.pace": "Allure",
  "stat.duration": "Durée",
  "stat.elevation": "Altitude",
  "stat.minElevation": "Altitude min",
  "stat.maxElevation": "Altitude max",
  "stat.grade": "Cotation ({activity})",

  "tabs.info": "Infos",
  "tabs.map": "Carte",
  "tabs.photos": "Photos",
  "tabs.places": "Lieux",
  "tabs.routes": "Itinéraires",
  "tabs.outings": "Sorties",
  "tabs.comments": "Commentaires",
  "tabs.subregions": "Sous-régions",
  "tabs.profile": "Profil",
  "tabs.followers": "Abonnés",
  "tabs.following": "Abonnements",
  "tabs.ticklist": "Projets",
  "tabs.members": "Membres",
  "tabs.events": "Événements",
  "tabs.participants": "Participants",

  "gate.needAccount": "Il vous faut un compte pour {action}. La consultation est ouverte à tous — la création demande une connexion.",
  "gateAction.logOuting": "enregistrer une sortie",
  "gateAction.keepTicklist": "tenir une liste de projets",
  "gateAction.keepSaved": "enregistrer des lieux sur la carte",
  "gateAction.seeProfile": "voir votre profil",
  "gateAction.seeCalendar": "voir votre calendrier",
  "gateAction.addPlace": "ajouter un lieu",
  "gateAction.drawRoute": "tracer un itinéraire",
  "gateAction.editOuting": "modifier une sortie",
  "gateAction.createGroup": "créer un groupe",
  "gateAction.editGroup": "modifier un groupe",
  "gateAction.createEvent": "planifier un événement",
  "gateAction.editEvent": "modifier un événement",

  "auth.email": "E-mail",
  "auth.password": "Mot de passe",
  "login.title": "Se connecter",
  "login.busy": "Connexion…",
  "login.failed": "Échec de la connexion",
  "login.forgot": "Mot de passe oublié ?",
  "login.noAccount": "Pas de compte ?",
  "login.demo": "Connexion démo : demo@gaea.dev / demo-password",
  "register.title": "S'inscrire",
  "register.displayName": "Nom affiché",
  "register.passwordMin": "Mot de passe (8 caractères min)",
  "register.tooShort": "Le mot de passe doit comporter au moins 8 caractères.",
  "register.busy": "Création du compte…",
  "register.submit": "Créer un compte",
  "register.failed": "Échec de l'inscription",
  "register.haveAccount": "Déjà un compte ?",
  "forgot.title": "Réinitialiser le mot de passe",
  "forgot.sent": "Si cette adresse correspond à un compte, un lien de réinitialisation a été envoyé.",
  "forgot.devNote": "En développement, le lien n'est pas envoyé par e-mail — il apparaît dans les logs du service d'authentification.",
  "forgot.intro": "Saisissez l'e-mail de votre compte et nous vous enverrons un lien pour réinitialiser votre mot de passe.",
  "forgot.busy": "Envoi…",
  "forgot.submit": "Envoyer le lien",
  "forgot.remembered": "Il vous est revenu ?",
  "forgot.failed": "Échec de la demande",
  "reset.title": "Choisir un nouveau mot de passe",
  "reset.invalid": "Ce lien de réinitialisation est invalide ou a expiré.",
  "reset.requestNew": "Demander un nouveau lien",
  "reset.newPassword": "Nouveau mot de passe (8 caractères min)",
  "reset.confirmPassword": "Confirmer le nouveau mot de passe",
  "reset.mismatch": "Les mots de passe ne correspondent pas.",
  "reset.busy": "Réinitialisation…",
  "reset.submit": "Réinitialiser le mot de passe",
  "reset.failed": "Échec de la réinitialisation",
  "verify.title": "Vérifier votre e-mail",
  "verify.invalid": "Ce lien de vérification est invalide ou a expiré.",
  "verify.requestNewPre": "Vous pouvez en demander un nouveau depuis",
  "verify.yourProfile": "votre profil",
  "verify.done": "Votre e-mail est vérifié. Merci !",
  "verify.goProfile": "Aller à votre profil",
  "verify.intro": "Cliquez ci-dessous pour confirmer cette adresse e-mail pour votre compte.",
  "verify.busy": "Confirmation…",
  "verify.submit": "Confirmer mon e-mail",
  "verify.failed": "Échec de la vérification",

  "feed.title": "Sorties récentes",
  "feed.everyone": "Tout le monde",
  "feed.following": "Abonnements",
  "feed.since": "Depuis",
  "feed.minRating": "Note min",
  "feed.sortNew": "Plus récentes",
  "feed.sortOld": "Plus anciennes",
  "feed.sortTop": "Mieux notées",
  "feed.updating": "Mise à jour…",
  "feed.loginToSeeFollowing": "pour voir les sorties des personnes que vous suivez.",
  "feed.notFollowingAnyone": "Vous ne suivez encore personne. Ouvrez une sortie et visitez le profil de son auteur pour le suivre.",
  "feed.emptyFollowing": "Aucune sortie des personnes que vous suivez ne correspond à ces filtres.",
  "feed.emptyAll": "Aucune sortie ne correspond à ces filtres.",
  "feed.loadingMore": "Chargement…",
  "feed.endOne": "C'est tout — {count} sortie.",
  "feed.endOther": "C'est tout — {count} sorties.",

  "card.privateTitle": "Privée — vous seul pouvez voir cette sortie",
  "card.privateAria": "Sortie privée",
  "card.followersTitle": "Visible uniquement par les abonnés du propriétaire",
  "card.followersAria": "Sortie réservée aux abonnés",
  "card.plannedTitle": "Sortie prévue — visible uniquement par vous",
  "card.moreRoutes": "+{count} de plus",
  "card.conditions": "Conditions : {value}",
  "card.commentsOne": "{count} commentaire",
  "card.commentsOther": "{count} commentaires",

  "like.ariaLike": "Aimer cette sortie ({count} j'aime)",
  "like.ariaUnlike": "Ne plus aimer cette sortie ({count} j'aime)",
  "like.ariaLogin": "Connectez-vous pour aimer cette sortie ({count} j'aime)",
  "follow.follow": "Suivre",
  "follow.following": "Suivi",

  "notif.title": "Notifications",
  "notif.ariaCount": "Notifications ({count} non lues)",
  "notif.markAll": "Tout marquer comme lu",
  "notif.marking": "Marquage…",
  "notif.loadError": "Impossible de charger les notifications.",
  "notif.empty": "Rien pour l'instant — les j'aime, commentaires et nouveaux abonnés apparaissent ici.",
  "notif.OUTING_LIKE": "a aimé votre sortie",
  "notif.OUTING_COMMENT": "a commenté votre sortie",
  "notif.PERSON_FOLLOW": "vous suit",
  "notif.CLUB_INVITE": "vous a invité à rejoindre un groupe",
  "notif.CLUB_JOIN_REQUEST": "souhaite rejoindre votre groupe",
  "notif.CLUB_JOIN_APPROVED": "a accepté votre demande d'adhésion",
  "notif.EVENT_INVITE": "vous a invité à un événement",

  "search.placeholder": "Chercher lieux, itinéraires, personnes…",
  "search.sectionPlaces": "Lieux",
  "search.sectionRegions": "Régions",
  "search.sectionRoutes": "Itinéraires",
  "search.sectionPeople": "Personnes",
  "search.sectionOutings": "Sorties",
  "search.nearbyPlaces": "Lieux à proximité",
  "search.nearbyRoutes": "Itinéraires à proximité",
  "search.nearMapCenter": "Près du centre de la carte",
  "search.nearMe": "Près de moi",
  "search.locating": "Localisation…",
  "search.geoError": "Position introuvable — autorisez la géolocalisation et réessayez.",
  "search.findingNearby": "Recherche à proximité…",
  "search.nearbyError": "Impossible de charger les résultats à proximité.",
  "search.nothingNearby": "Rien à proximité.",
  "search.searching": "Recherche…",
  "search.noMatches": "Aucun résultat.",
  "search.showOnMap": "Voir sur la carte",
  "search.person": "Personne",
  "search.unnamed": "Sans nom",
  "search.away": "à {distance}",

  "map.addPlaceHere": "Ajouter un lieu ici ?",
  "map.add": "Ajouter",
  "map.planEventHere": "Planifier un événement ici",
  "map.saveSpotHere": "Enregistrer ce point",
  "map.spotNamePlaceholder": "p. ex. Point de vue secret",
  "map.savedPlaces": "Enregistrés",
  "map.savedEmpty": "Rien d'enregistré — utilisez « Enregistrer » sur la page d'un lieu ou faites un clic droit sur la carte.",
  "map.manageSaved": "Tous les lieux enregistrés",
  "map.filters": "Filtres",
  "map.drawRoute": "Tracer un itinéraire",
  "map.zoomForData": "Zoomez pour voir les données de la carte",
  "map.zoomForRoutes": "Zoomez pour voir les itinéraires",
  "map.truncated": "{shown} sur {total} {layer}",
  "map.truncatedMore": " (+{count} de plus)",
  "map.truncatedZoom": " — zoomez pour le reste",
  "map.loadError": "Échec du chargement des données de la carte",
  "map.layerPlaces": "lieux",
  "map.layerRoutes": "itinéraires",
  "map.layerRegions": "régions",
  "map.layerOutings": "sorties",
  "map.layerEvents": "événements",

  "filters.title": "Affichage & filtres",
  "filters.closeAria": "Fermer les filtres",
  "filters.showOnMap": "Afficher sur la carte",
  "filters.outings": "Sorties",
  "filters.routes": "Itinéraires",
  "filters.routeTracks": "Tracés",
  "filters.regions": "Régions",
  "filters.events": "Événements",
  "filters.tracksHint": "Les tracés apparaissent à partir du zoom 11 — zoomez si vous ne les voyez pas.",
  "filters.clustering": "Regrouper les épingles proches",
  "filters.routeActivities": "Activités des itinéraires",
  "filters.placeTypes": "Types de lieux",
  "filters.elevMin": "Altitude du lieu ≥ (m)",
  "filters.elevMax": "Altitude du lieu ≤ (m)",
  "filters.outingsFrom": "Sorties depuis",
  "filters.outingsUntil": "Sorties jusqu'au",
  "filters.distMin": "Distance de l'itinéraire ≥ (km)",
  "filters.distMax": "Distance de l'itinéraire ≤ (km)",
  "filters.ascentMin": "Dénivelé de l'itinéraire ≥ (m)",
  "filters.nameContains": "Le nom de l'itinéraire contient",
  "filters.clear": "Effacer les filtres",

  "tooltip.clickForDetails": "Cliquer pour les détails",

  "profileMap.title": "Carte d'activité",
  "profileMap.outingsOne": "{count} sortie",
  "profileMap.outingsOther": "{count} sorties",
  "profileMap.w30d": "30 jours",
  "profileMap.w3m": "3 mois",
  "profileMap.w12m": "12 mois",
  "profileMap.wAll": "Tout",
  "profileMap.empty": "Aucune sortie cartographiée sur cette période.",

  "profile.loadError": "Impossible de charger votre profil.",
  "profile.editName": "Modifier le nom affiché",
  "profile.publicProfile": "Profil public",
  "profile.logout": "Se déconnecter",
  "profile.myOutings": "Mes sorties",
  "profile.noOutings": "Aucune sortie — enregistrez la première depuis une page d'itinéraire ou l'onglet Nouveau.",
  "profile.joinedAs": "Participations",
  "profile.changePassword": "Changer de mot de passe",
  "avatar.change": "Changer la photo",
  "avatar.upload": "Ajouter une photo",
  "avatar.uploadFailed": "Échec de l'envoi",
  "email.verified": "vérifié",
  "email.notVerified": "E-mail non vérifié",
  "email.sentNote": "Envoyé — vérifiez votre boîte mail. (En développement, le lien n'est pas envoyé par e-mail — il apparaît dans les logs du service d'authentification.)",
  "email.send": "Envoyer l'e-mail de vérification",
  "email.sending": "Envoi…",
  "email.sendFailed": "Impossible d'envoyer l'e-mail",
  "pw.current": "Mot de passe actuel",
  "pw.new": "Nouveau mot de passe (8 caractères min)",
  "pw.confirm": "Confirmer le nouveau mot de passe",
  "pw.tooShort": "Le nouveau mot de passe doit comporter au moins 8 caractères.",
  "pw.mismatch": "Les nouveaux mots de passe ne correspondent pas.",
  "pw.wrongCurrent": "Le mot de passe actuel est incorrect.",
  "pw.failed": "Échec du changement de mot de passe",
  "pw.changed": "Mot de passe changé.",
  "pw.changing": "Changement…",
  "pw.submit": "Changer le mot de passe",

  "person.notFound": "Personne introuvable.",
  "person.joined": "Membre depuis {date}",
  "person.activities": "Activités",
  "person.noneYetSelf": "Aucune pour l'instant — choisissez vos activités depuis votre profil.",
  "person.noneListed": "Aucune indiquée.",
  "person.noOutings": "Aucune sortie enregistrée.",
  "person.noFollowers": "Pas encore d'abonnés.",
  "person.notFollowingSelf": "Vous ne suivez encore personne.",
  "person.notFollowing": "Ne suit encore personne.",

  "ticklist.title": "Projets",
  "ticklist.intro": "Les itinéraires que vous voulez faire. Les projets marqués publics apparaissent sur votre profil — le reste n'est visible que par vous.",
  "ticklist.empty": "Rien dans vos projets — ouvrez un itinéraire et cliquez sur « Projets ».",
  "ticklist.browseMap": "Parcourir la carte",
  "ticklist.done": "Fait",
  "ticklist.doneOn": "Fait le {date}",
  "ticklist.log": "Enregistrer",

  "savePlace.button": "Enregistrer",
  "savePlace.saved": "Enregistré",
  "savePlace.buttonTitle": "Enregistrer ce lieu pour un accès rapide sur la carte",
  "savePlace.savedTitle": "Retirer de vos lieux enregistrés",
  "saved.title": "Lieux enregistrés",
  "saved.intro": "Lieux et points de carte enregistrés pour un accès rapide. Vous seul pouvez les voir.",
  "saved.tabPlaces": "Lieux",
  "saved.tabActivity": "Activité à proximité",
  "saved.empty": "Rien d'enregistré pour l'instant — cliquez sur « Enregistrer » sur la page d'un lieu, ou faites un clic droit sur la carte.",
  "saved.browseMap": "Parcourir la carte",
  "saved.customPin": "Point personnalisé",
  "saved.showOnMap": "Carte",
  "saved.showOnMapTitle": "Voir sur la carte",
  "saved.savedOn": "Enregistré le {date}",
  "saved.allSaved": "Tous",
  "saved.nearbyNote": "Sorties récentes à ~10 km de vos lieux enregistrés.",
  "saved.emptyActivity": "Pas encore de sorties près de vos lieux enregistrés.",
  "saved.renameTitle": "Renommer",
  "saved.removeTitle": "Retirer des enregistrés",
  "saved.latest": "Affiche les {count} dernières sorties.",
  "tick.priorityAria": "Priorité",
  "tick.publicTitle": "Public — visible sur votre profil. Cliquer pour le rendre privé.",
  "tick.privateTitle": "Privé — visible par vous seul. Cliquer pour l'afficher sur votre profil.",
  "tick.makePrivate": "Rendre le projet privé",
  "tick.makePublic": "Rendre le projet public",
  "tick.removeTitle": "Retirer des projets",

  "tick.button": "Projets",
  "tick.priority": "Priorité",
  "tick.targetSeason": "Saison visée",
  "tick.seasonPlaceholder": "été 2027",
  "tick.note": "Note privée",
  "tick.notePlaceholder": "Partenaire, matériel, conditions à attendre…",
  "tick.add": "Ajouter aux projets",

  "place.notFound": "Lieu introuvable.",
  "place.addedBy": "Ajouté par",
  "place.editPlace": "Modifier le lieu",
  "place.confirmDelete": "Supprimer ce lieu ? Cette action est irréversible.",
  "place.elevationOptional": "Altitude (m, facultatif)",
  "place.sortLinked": "Liés d'abord",
  "place.sortDistance": "Distance (plus courte)",
  "place.sortAscent": "Dénivelé (plus grand)",
  "place.linked": "lié",
  "place.passes": "passe à {distance}",
  "place.noRoutes": "Aucun itinéraire ne passe encore par ce lieu.",

  "newPlace.heading": "Nouveau lieu",
  "newPlace.title": "Ajouter un lieu",
  "newPlace.pickPre": "Choisissez d'abord un endroit — clic droit (ou appui long) sur la",
  "newPlace.pickMap": "carte",
  "newPlace.pickPost": "là où se trouve le lieu.",
  "newPlace.namePlaceholder": "ex. Sněžka",
  "newPlace.busy": "Ajout…",
  "newPlace.submit": "Ajouter le lieu",

  "region.kicker": "Région",
  "region.notFound": "Région introuvable.",
  "region.partOf": "Fait partie de",
  "region.noSubregions": "Aucune sous-région.",
  "region.sortElevation": "Altitude (plus haute)",
  "region.noPlaces": "Aucun lieu recensé dans cette région pour l'instant (l'appartenance est calculée d'après la géométrie).",

  "route.kicker": "Itinéraire",
  "route.notFound": "Itinéraire introuvable.",
  "route.editDetails": "Modifier les détails",
  "route.logOuting": "Enregistrer une sortie",
  "route.ratingsOne": "({count} note)",
  "route.ratingsOther": "({count} notes)",
  "route.wantOne": "{count} personne veut faire cet itinéraire",
  "route.wantOther": "{count} personnes veulent faire cet itinéraire",
  "route.addedBy": "Ajouté par",
  "route.aMember": "un membre",
  "route.related": "Itinéraires liés",
  "route.noPlaces": "Aucun lieu lié à cet itinéraire pour l'instant.",
  "route.photoSample": "Échantillon des sorties récentes — ouvrez une sortie pour toutes ses photos.",
  "route.noPhotos": "Pas encore de photos — les photos des sorties sur cet itinéraire apparaissent ici.",
  "route.noOutings": "Aucune sortie sur cet itinéraire pour l'instant.",
  "route.beFirst": "Soyez le premier !",
  "route.confirmDelete": "Supprimer l'itinéraire « {name} » ? Cette action est irréversible.",
  "route.fkDelete": "Cet itinéraire est référencé par des sorties et ne peut pas être supprimé.",

  "outing.kicker": "Sortie",
  "outing.notFound": "Sortie introuvable.",
  "outing.confirmDelete": "Supprimer cette sortie ? Cette action est irréversible.",
  "outing.privateTitle": "Vous seul pouvez voir cette sortie",
  "outing.followersTitle": "Visible uniquement par les abonnés du propriétaire",
  "outing.plannedTitle": "Prévue — visible uniquement par vous jusqu'à ce que vous la marquiez comme terminée",
  "outing.markCompleted": "Marquer comme terminée",
  "outing.party": "Groupe",
  "outing.noGeometry": "Ni trace ni géométrie d'itinéraire pour cette sortie.",
  "outing.noPhotos": "Aucune photo pour cette sortie.",
  "outing.noRoutes": "Aucun itinéraire lié à cette sortie.",
  "outing.edit": "Modifier",

  "comments.back": "← Retour à tous les commentaires",
  "comments.replyingUnder": "Réponse sous",
  "comments.deleted": "[supprimé]",
  "comments.empty": "Pas encore de commentaires. Lancez la conversation.",
  "comments.author": "Auteur",
  "comments.wasDeleted": "Ce commentaire a été supprimé.",
  "comments.reply": "Répondre",
  "comments.confirmDelete": "Supprimer ce commentaire ? Les réponses resteront visibles.",
  "comments.continueOne": "Poursuivre ce fil ({count} réponse)",
  "comments.continueOther": "Poursuivre ce fil ({count} réponses)",
  "comments.replyPlaceholder": "Écrire une réponse…",
  "comments.addPlaceholder": "Ajouter un commentaire…",
  "comments.posting": "Publication…",
  "comments.submit": "Commenter",
  "comments.loginToJoin": "pour participer à la discussion.",

  "form.title": "Titre",
  "form.name": "Nom",
  "form.date": "Date",
  "form.activity": "Activité",
  "form.activities": "Activités",
  "form.conditions": "Conditions",
  "form.rating": "Note",
  "form.notes": "Notes",
  "form.description": "Description",
  "form.visibility": "Visibilité",
  "form.status": "Statut",
  "form.durationOptional": "Durée (minutes, facultatif)",
  "form.pickActivity": "Choisissez au moins une activité.",

  "newOuting.title": "Enregistrer une sortie",
  "newOuting.titlePlaceholder": "ex. Boucle du Sněžka au lever du soleil",
  "newOuting.routesOptional": "Itinéraires (facultatif)",
  "newOuting.gpxOptional": "Trace GPX (facultatif)",
  "newOuting.timeHours": "Temps (heures)",
  "newOuting.notesPlaceholder": "Conditions, moments forts, avertissements pour les autres…",
  "newOuting.photosOptional": "Photos (facultatif)",
  "newOuting.participantsOptional": "Participants (facultatif)",
  "newOuting.followersHint": "Abonnés : uniquement les personnes qui vous suivent.",
  "newOuting.plannedHint": "Une sortie prévue n'est visible que par vous jusqu'à ce que vous la marquiez comme terminée.",
  "newOuting.ownerNote": "Vous serez enregistré comme propriétaire.",
  "newOuting.submit": "Enregistrer la sortie",
  "newOuting.errTitle": "Donnez un titre à votre sortie.",
  "newOuting.errDate": "Quand y êtes-vous allé ?",
  "newOuting.errFuture": "Une sortie terminée ne peut pas être dans le futur — marquez-la plutôt comme prévue.",
  "newOuting.errElapsed": "Le temps doit être un nombre d'heures positif.",
  "err.owner": "Un problème est survenu lors de votre enregistrement comme propriétaire de cette sortie. Réessayez.",
  "err.permission": "Vous n'avez pas la permission de faire cela — essayez de vous reconnecter.",
  "err.rejected": "Le serveur a rejeté les données : {msg}",
  "gpx.stats": "{count} points · ~{distance}",
  "gpx.removeAria": "Retirer le fichier GPX",
  "edit.title": "Modifier la sortie",
  "edit.notOwner": "Seul le propriétaire de cette sortie peut la modifier.",
  "edit.noPermission": "Vous n'avez pas la permission de modifier cette sortie.",
  "edit.gpx": "Trace GPX",
  "edit.replacesOnSave": "remplacera la trace actuelle à l'enregistrement",
  "edit.cancelTrackAria": "Annuler le changement de trace",
  "edit.trackRemoved": "La trace sera retirée à l'enregistrement.",
  "edit.trackAttached": "Trace jointe · {distance} / {ascent} ↑",
  "edit.replace": "Remplacer",
  "edit.photos": "Photos",
  "edit.routes": "Itinéraires",
  "edit.participants": "Participants",
  "edit.submit": "Enregistrer les modifications",
  "photo.removedUndo": "Retirée — annuler",
  "photo.removeAria": "Retirer la photo",
  "photo.caption": "Légende (facultatif)",
  "photo.add": "Ajouter des photos",
  "photo.addMore": "Ajouter d'autres photos",
  "photo.closeAria": "Fermer la photo",
  "photo.label": "Photo",

  "newRoute.heading": "Nouvel itinéraire",
  "newRoute.title": "Tracer un itinéraire",
  "newRoute.drawTab": "Tracer sur la carte",
  "newRoute.uploadTab": "Importer un GPX",
  "newRoute.gpxFile": "Fichier GPX",
  "newRoute.pointsOne": "{count} point",
  "newRoute.pointsOther": "{count} points",
  "newRoute.undo": "Annuler",
  "newRoute.undoTitle": "Annuler le dernier point (Ctrl+Z)",
  "newRoute.clear": "Effacer",
  "newRoute.drawHelp": "Cliquez pour ajouter des points · faites glisser un point pour le déplacer · cliquez sur un point puis Suppr pour le retirer",
  "newRoute.uploadHint": "Importez un fichier GPX pour le prévisualiser ici.",
  "newRoute.noElevNote": "Les itinéraires tracés n'ont pas de données d'altitude — le dénivelé affiche 0. L'altitude vient des imports GPX.",
  "newRoute.namePlaceholder": "ex. Sněžka par Obří důl",
  "newRoute.linkedPlaces": "Lieux liés (facultatif)",
  "newRoute.errName": "Donnez un nom à l'itinéraire.",
  "newRoute.errGeomDraw": "Tracez au moins deux points sur la carte.",
  "newRoute.errGeomUpload": "Importez d'abord un fichier GPX.",
  "newRoute.errDuration": "La durée doit être un nombre de minutes positif.",
  "newRoute.busy": "Création…",
  "newRoute.submit": "Créer l'itinéraire",

  "group.kicker": "Groupe",
  "group.notFound": "Groupe introuvable.",
  "group.membersOne": "{count} membre",
  "group.membersOther": "{count} membres",
  "group.created": "Créé le {date}",
  "group.joinPolicy": "Politique d'adhésion",
  "group.join": "Rejoindre le groupe",
  "group.requestJoin": "Demander à rejoindre",
  "group.requestPending": "Demande en attente",
  "group.withdrawRequest": "Retirer la demande",
  "group.inviteOnlyNote": "Sur invitation — un admin doit vous inviter.",
  "group.invitedBanner": "Vous avez été invité dans ce groupe.",
  "group.acceptInvite": "Accepter l'invitation",
  "group.decline": "Refuser",
  "group.leave": "Quitter le groupe",
  "group.confirmLeave": "Quitter ce groupe ?",
  "group.bannedNote": "Vous avez été banni de ce groupe.",
  "group.edit": "Modifier le groupe",
  "group.confirmDelete": "Supprimer ce groupe ? Cette action est irréversible.",
  "group.noDescription": "Pas encore de description.",
  "group.noOutings": "Aucune sortie partagée dans ce groupe pour l'instant.",
  "group.membersHidden": "La liste des membres n'est visible que par les membres.",
  "group.eventsSoon": "Les événements de groupe arrivent bientôt.",
  "group.pendingRequests": "Demandes en attente",
  "group.approve": "Approuver",
  "group.promote": "Nommer admin",
  "group.demote": "Retirer le rôle d'admin",
  "group.ban": "Bannir",
  "group.unban": "Débannir",
  "group.confirmBan": "Bannir {name} de ce groupe ?",
  "group.confirmKick": "Retirer {name} de ce groupe ?",
  "group.invite": "Inviter des personnes",
  "group.newEvent": "Nouvel événement",
  "group.noUpcomingEvents": "Aucun événement à venir.",

  "newGroup.title": "Créer un groupe",
  "newGroup.namePlaceholder": "ex. Alpine Wanderers",
  "newGroup.errName": "Donnez un nom au groupe.",
  "newGroup.visibilityHint": "Masqué : seuls les membres peuvent voir le groupe.",
  "newGroup.ownerNote": "Vous serez enregistré comme propriétaire du groupe.",
  "newGroup.submit": "Créer le groupe",
  "editGroup.adminsOnly": "Seuls les admins du groupe peuvent le modifier.",

  "share.label": "Partager dans un groupe",
  "share.select": "Choisir un groupe…",
  "share.removeAria": "Ne plus partager avec {name}",
  "share.button": "Partager",
  "share.copied": "Lien copié",

  "event.kicker": "Événement",
  "event.notFound": "Événement introuvable.",
  "event.invitedBanner": "Vous avez été invité à cet événement.",
  "event.acceptInvite": "Accepter l'invitation",
  "event.join": "Participer",
  "event.requestJoin": "Demander à participer",
  "event.requestPending": "Demande en attente",
  "event.withdrawRequest": "Retirer la demande",
  "event.youAreGoing": "Vous participez",
  "event.cantGo": "Me désister",
  "event.youDeclined": "Vous avez décliné",
  "event.imGoing": "Je participe",
  "event.membersOnlyPre": "Seuls les membres de",
  "event.theGroup": "ce groupe",
  "event.membersOnlyPost": "peuvent rejoindre cet événement.",
  "event.inviteOnlyNote": "Sur invitation — un organisateur doit vous inviter.",
  "event.closedNote": "La participation est gérée par les organisateurs.",
  "event.addToCalendar": "Ajouter au calendrier",
  "event.edit": "Modifier",
  "event.confirmDelete": "Supprimer cet événement ? Cette action est irréversible.",
  "event.repeats": "Répétition : {freq}",
  "event.repeatsUntil": "Répétition : {freq} (jusqu'au {date})",
  "event.organizedBy": "Organisé par",
  "event.goingCount": "{count} inscrits",
  "event.goingCap": "{count} / {cap} inscrits",
  "event.fullHint": "Complet",
  "event.noDescription": "Pas encore de description.",
  "event.maxParticipants": "Participants max",
  "event.recurrence": "Répétition",
  "event.meetingPoint": "Point de rendez-vous",
  "event.noRoutes": "Aucun itinéraire prévu pour cet événement pour l'instant.",
  "event.noGeometry": "Ni position ni géométrie d'itinéraire pour cet événement.",
  "event.organizers": "Organisateurs",
  "event.goingSection": "Inscrits",
  "event.nobodyYet": "Personne ne s'est encore inscrit.",
  "event.confirmRemove": "Retirer {name} de cet événement ?",

  "calendar.today": "Aujourd'hui",
  "calendar.prevMonth": "Mois précédent",
  "calendar.nextMonth": "Mois suivant",
  "calendar.legendOwn": "Vos sorties",
  "calendar.legendFollowed": "Personnes que vous suivez",
  "calendar.legendEvent": "Événements",
  "calendar.more": "+{count} de plus",
  "calendar.dayEmpty": "Rien ce jour-là.",
  "calendar.monthEmpty": "Rien ce mois-ci pour l'instant.",

  "discover.title": "Découvrir",
  "discover.groups": "Groupes",
  "discover.events": "Événements",
  "discover.searchGroups": "Rechercher des groupes…",
  "discover.searchEvents": "Rechercher des événements…",
  "discover.newGroup": "Nouveau groupe",
  "discover.newEvent": "Nouvel événement",
  "discover.yourGroups": "Vos groupes",
  "discover.yourEvents": "Vos événements",
  "discover.invited": "Invitation",
  "discover.hiddenGroup": "Groupe masqué",
  "discover.hiddenEvent": "Événement masqué",
  "discover.noGroups": "Aucun groupe trouvé.",
  "discover.noEvents": "Aucun événement à venir trouvé.",
  "discover.myGroupsOnly": "Dans mes groupes",
  "discover.notInAnyGroup": "Vous ne faites encore partie d'aucun groupe.",

  "newEvent.title": "Planifier un événement",
  "newEvent.titlePlaceholder": "ex. Randonnée au lever du soleil à la Croix de Fer",
  "newEvent.descriptionPlaceholder": "Programme, détails du rendez-vous, quoi apporter…",
  "newEvent.start": "Début",
  "newEvent.endOptional": "Fin (facultatif)",
  "newEvent.groupOptional": "Groupe (facultatif)",
  "newEvent.noGroup": "Sans groupe — événement indépendant",
  "newEvent.groupHint": "Choisir un groupe débloque la visibilité et l'adhésion réservées aux membres.",
  "newEvent.repeatsUntil": "Répéter jusqu'au",
  "newEvent.maxOptional": "Participants max (facultatif)",
  "newEvent.meetingPointOptional": "Point de rendez-vous (facultatif)",
  "newEvent.mapPoint": "Point sur la carte : {lat}, {lng}",
  "newEvent.clearPointAria": "Retirer le point de la carte",
  "newEvent.showOnMap": "Afficher sur la carte",
  "newEvent.showOnMapHint": "Les événements à venir avec un point de rendez-vous apparaissent sur la carte.",
  "newEvent.routesOptional": "Itinéraires prévus (facultatif)",
  "newEvent.organizerNote": "Vous serez enregistré comme organisateur.",
  "newEvent.submit": "Créer l'événement",
  "newEvent.errTitle": "Donnez un titre à l'événement.",
  "newEvent.errStart": "Quand commence-t-il ?",
  "newEvent.errEnd": "La fin doit être après le début.",
  "newEvent.errCap": "Participants max doit être un nombre positif.",
  "editEvent.title": "Modifier l'événement",
  "editEvent.notOrganizer": "Seuls les organisateurs peuvent modifier cet événement.",
  "editEvent.noPermission": "Vous n'avez pas la permission de modifier cet événement.",

  "admin.title": "File de modération",
  "admin.reportsOne": "{count} signalement",
  "admin.reportsOther": "{count} signalements",
  "admin.all": "Tous",
  "admin.accountUpdated": "Statut du compte mis à jour.",
  "admin.loadingReports": "Chargement des signalements…",
  "admin.noReports": "Aucun signalement correspondant.",
  "admin.view": "Voir : {kind}",
  "admin.reportedBy": "Signalé par",
  "admin.resolve": "Résoudre",
  "admin.dismiss": "Rejeter",
  "admin.account": "Compte : {status}",
  "admin.suspend": "Suspendre",
  "admin.reinstate": "Rétablir",
  "admin.confirmSuspend": "Suspendre ce compte ? La personne perdra l'accès jusqu'à son rétablissement.",
  "admin.confirmReinstate": "Rétablir ce compte (statut de nouveau actif) ?",
  "admin.adminsOnly": "Réservé aux admins — cette page montre la file de modération des administrateurs du site.",

  "viewOnMap.button": "Voir sur la carte",

  "report.button": "Signaler ce contenu",
  "report.dialogAria": "Signaler un contenu",
  "report.heading": "Signaler ce contenu",
  "report.thanks": "Merci, nous allons regarder.",
  "report.intro": "Dites-nous ce qui ne va pas — un modérateur l'examinera.",
  "report.placeholder": "Quel est le problème ? (au moins 10 caractères)",
  "report.minChars": "{count}/{min} caractères minimum",
  "report.chars": "{count}/{max} caractères",
  "report.busy": "Signalement…",
  "report.submit": "Signaler",

  "export.busy": "Export…",

  "chart.axisAria": "Axe X du profil",
  "chart.aria": "Profil altimétrique : {distance}, de {min} à {max}",
  "chart.min": "Min",
  "chart.max": "Max",

  "picker.searchPeople": "Chercher des personnes par nom…",
  "picker.noPeople": "Aucune personne trouvée.",
  "picker.removePersonAria": "Retirer {name}",
  "picker.searchPlaces": "Chercher des lieux par nom…",
  "picker.linkAnotherPlace": "Lier un autre lieu…",
  "picker.noPlaces": "Aucun lieu trouvé.",
  "picker.removePlaceAria": "Retirer le lieu {name}",
  "picker.searchRoutes": "Chercher des itinéraires par nom…",
  "picker.addAnotherRoute": "Ajouter un autre itinéraire…",
  "picker.noRoutes": "Aucun itinéraire trouvé.",
  "picker.removeRouteAria": "Retirer l'itinéraire {name}",
  "picker.styleAria": "Style",
  "picker.stylePlaceholder": "Style…",
  "picker.resultAria": "Résultat",
  "picker.resultPlaceholder": "Résultat (ex. Sommet)",

  "stars.one": "{count} étoile",
  "stars.other": "{count} étoiles",

  "error.somethingWrong": "Une erreur est survenue.",
  "error.reload": "Recharger",
} satisfies Record<MessageKey, string>;

export const dictionaries = { en, de, fr } as const;
